import { TERMINAL_TASK_STATUSES, getSequentialActivitiesForType, isTerminatingEngagementStatus, getCarePlanCategoryDisplay, resolveActivityCode as resolveActivityCode$1, GP_ROLE_TYPES, GP_ROLE_TYPE_EXTENSION, CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL, PATIENT_ENGAGEMENT_STATUS_SYSTEM, PATIENT_ENGAGEMENT_STATUS_OPTIONS, LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM, LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS, PAUSE_FLAG_REASON_EXTENSION_URL, SCREENER_FOLLOWUPS_PAUSED_FLAG_CODE, FLAG_LOCAL_CODE_SYSTEM, ICD10_CM_SYSTEM, CONDITION_CLINICAL_STATUS_SYSTEM } from './constants.mjs';
import { updateCarePlanActivity, buildCancelledCarePlan, updatePatientEngagementStatus, createCarePlan, buildCompletedCarePlan, completeCarePlan, reconcileCarePlanReadyState, APPROVE_SELECTED_SERVICES_CODE, clearServiceApprovalNotificationForTask, upsertSubscriber } from './actions.mjs';
import { getActivityTasks, resolveActivityOwner, submitBundle, getActiveOperationalCarePlans, hasGeneratedCadencedFollowups, resolveOwnerOverridesForPlan, updateResource, findExistingCoCMFlag, findOpenTasksForPatient, getActiveCarePlans, getCarePlanByType, buildCarePlanTasksMap, fetchExistingActiveFlags, getOnHoldCarePlans, findAllActivePauseFlags, findAllOnHoldCarePlans, findPatientPauseFlagHistory, findPatientActivePauseFlags, createResource, searchProblemListConditions, createCondition, updateCondition } from './services.mjs';
import { buildCancelledTaskEntries, buildCarePlanBundle, buildCreatePatientBundle, buildCoCMCancellationBundle, buildCoCMEnrollmentBundle, buildIntakeCancellationBundle, buildIntakeCompletionBundle, buildCoCMCompletionBundle, buildPausePatientBundle, buildUnpausePatientBundle, buildUpdatePatientBundle, buildProblemListCondition } from './builders.mjs';
import { extractSingleResourceFromTransactionResult, extractResourcesFromTransactionResult, toLogSafeError, localCalendarDateTodayISO } from './utils.mjs';
import { globalEventEmitter, DomainEventNames } from './events.mjs';
import { getPatientDisplayName, getPatientEngagementStatus as getPatientEngagementStatus$1, resolveEngagementStatusFromCarePlans, areAllActivitiesCompleted, getCarePlanType, shouldEngagePatientOnIntakeCompletion, getPrePauseCarePlanStatus as getPrePauseCarePlanStatus$1, isPauseFlag } from './resolvers.mjs';
import { validateCoCMCarePlanCreation, validateTriageCarePlanCreation } from './validators.mjs';
import { buildOrganizationReference } from './adapters.mjs';
import { toOrganizationId, COVERAGE_APPLICABILITY_PRIMARY, COVERAGE_APPLICABILITY_SECONDARY } from './types.mjs';

/** Task statuses that represent a "not yet active" state (eligible for advancement) */ const ADVANCEABLE_TASK_STATUSES = new Set([
    "draft",
    "ready",
    "requested"
]);
/** Legacy detail.status values that represent a terminal activity state. */ const TERMINAL_DETAIL_STATUSES = new Set([
    "completed",
    "cancelled",
    "stopped"
]);
/**
 * Check if an activity is in a terminal (completed/cancelled/archived) state.
 *
 * Archived activities are stored as `Task.status: "cancelled"` and remain in
 * `CarePlan.activity[]`. When such an activity sits between a just-completed
 * activity and the genuinely-next draft, advancement must skip past it — a
 * cancelled/archived occurrence of a repeatable activity (e.g. a
 * `follow-up-on-cocm` the clinician archived) must not strand the newly-added
 * occurrence in draft.
 */ const isActivityTerminal = (activity, tasksById)=>{
    if (activity.detail?.status) {
        return TERMINAL_DETAIL_STATUSES.has(activity.detail.status);
    }
    const ref = activity.reference?.reference;
    if (!ref) return false;
    const taskId = ref.startsWith("Task/") ? ref.slice(5) : ref;
    const task = tasksById.get(taskId);
    return task ? TERMINAL_TASK_STATUSES.has(task.status ?? "") : false;
};
/**
 * Resolve activity code for a CarePlan activity entry.
 *
 * For legacy (detail-based): reads activity.detail.code
 * For Task-backed: looks up the linked Task and reads Task.code
 */ const resolveActivityCode = (activity, tasksById)=>{
    const detailCode = activity.detail?.code?.coding?.[0]?.code;
    if (detailCode) return detailCode;
    const ref = activity.reference?.reference;
    if (!ref) return undefined;
    const taskId = ref.startsWith("Task/") ? ref.slice(5) : ref;
    const task = tasksById.get(taskId);
    return task?.code?.coding?.[0]?.code;
};
/**
 * Extract the linked `Task/{id}` reference for an activity entry, if any.
 * Used to update the exact occurrence of a multi-occurrence activity rather
 * than relying on an ambiguous code-only lookup.
 * @agentic @benchmark/core
 */ const resolveActivityTaskId = (activity)=>{
    const ref = activity.reference?.reference;
    return ref?.startsWith("Task/") ? ref : undefined;
};
/**
 * Check if an activity is in a "not started" state eligible for advancement.
 *
 * For legacy: checks detail.status === "not-started"
 * For Task-backed: checks Task.status is draft/ready/requested
 */ const isActivityAdvanceable = (activity, tasksById)=>{
    // Legacy detail-based
    if (activity.detail?.status) {
        return activity.detail.status === "not-started";
    }
    // Task-backed
    const ref = activity.reference?.reference;
    if (!ref) return false;
    const taskId = ref.startsWith("Task/") ? ref.slice(5) : ref;
    const task = tasksById.get(taskId);
    return task ? ADVANCEABLE_TASK_STATUSES.has(task.status ?? "") : false;
};
/**
 * Build a tasksById map for a CarePlan.
 * Returns empty map for legacy (detail-based) CarePlans.
 */ const buildTasksMap = async (medplum, carePlan)=>{
    const hasTaskBacked = carePlan.activity?.some((a)=>a.reference?.reference && !a.detail?.code);
    if (!hasTaskBacked || !carePlan.id) {
        return new Map();
    }
    const tasks = await getActivityTasks(medplum, carePlan.id);
    return new Map(tasks.filter((t)=>t.id).map((t)=>[
            t.id,
            t
        ]));
};
/**
 * Resolves the next activity's owner through the full waterfall:
 * 1. User override from completion data (highest precedence)
 * 2. Assignment mode default + business rule overrides (fresh resolution)
 * @agentic connections
 */ const resolveNextActivityOwner = async (medplum, nextActivityCode, carePlan, completionData)=>{
    const userOverrideRef = completionData?.nextActivityOwnerRef;
    const userOverrideDisplay = completionData?.nextActivityOwnerDisplay;
    if (userOverrideRef && userOverrideDisplay) {
        return {
            ref: userOverrideRef,
            display: userOverrideDisplay
        };
    }
    const patientRef = carePlan.subject?.reference?.replace("Patient/", "");
    if (!patientRef) return undefined;
    try {
        const patient = await medplum.readResource("Patient", patientRef);
        if (!patient) return undefined;
        return resolveActivityOwner(medplum, {
            activityCode: nextActivityCode,
            patient
        });
    } catch  {
        return undefined;
    }
};
/**
 * Auto-advance the first activity to ready
 *
 * When a care plan is activated, mark the first activity as ready
 * (actionable, awaiting practitioner). `in-progress` is reserved for
 * when the practitioner explicitly begins work.
 *
 * @param medplum - MedplumClient instance
 * @param carePlan - Care plan to advance
 * @returns Updated CarePlan with first activity ready
 */ async function advanceFirstActivityToReady(medplum, carePlan) {
    if (!carePlan.activity || carePlan.activity.length === 0) {
        return carePlan;
    }
    const tasksById = await buildTasksMap(medplum, carePlan);
    const firstActivity = carePlan.activity[0];
    const firstActivityCode = resolveActivityCode(firstActivity, tasksById);
    if (!firstActivityCode) {
        return carePlan;
    }
    if (isActivityAdvanceable(firstActivity, tasksById)) {
        return updateCarePlanActivity(medplum, {
            carePlan,
            activityCode: firstActivityCode,
            taskId: resolveActivityTaskId(firstActivity),
            newStatus: "ready"
        });
    }
    return carePlan;
}
/**
 * Auto-advance the next activity when a prior activity completes
 *
 * When an activity is marked as completed, automatically advance the
 * following activity to ready. Supports both legacy and Task-backed plans.
 *
 * @param medplum - MedplumClient instance
 * @param carePlan - Care plan containing the activity
 * @param completedActivityCode - Code of the activity that was completed
 * @param completionData - Optional next-activity owner/due-date overrides
 * @param completedTaskId - Exact Task id (or `Task/{id}`) of the completed
 *   activity. When provided, the completed activity's position is resolved by
 *   reference rather than code, so multi-occurrence activities (e.g. cadenced
 *   followups) advance from the correct entry instead of the first match.
 * @returns Updated CarePlan with next activity advanced
 */ async function advanceToNextActivity(medplum, carePlan, completedActivityCode, completionData, completedTaskId) {
    if (!carePlan.activity || carePlan.activity.length === 0) {
        return carePlan;
    }
    const carePlanType = carePlan.category?.[0]?.coding?.[0]?.code;
    if (!carePlanType || carePlanType !== "intake" && carePlanType !== "triage" && carePlanType !== "cocm") {
        return carePlan;
    }
    const tasksById = await buildTasksMap(medplum, carePlan);
    // Find the completed activity's position. Prefer an exact Task-reference
    // match (disambiguates multi-occurrence activities such as the 6/12/18-week
    // cadenced followups), then fall back to a code match for legacy plans.
    const completedTaskRef = completedTaskId ? completedTaskId.startsWith("Task/") ? completedTaskId : `Task/${completedTaskId}` : undefined;
    let completedActivityIndex = completedTaskRef ? carePlan.activity.findIndex((activity)=>activity.reference?.reference === completedTaskRef) : -1;
    if (completedActivityIndex === -1) {
        // No exact Task-reference match (e.g. completion path did not thread a
        // taskId). Resolve by activity code, but disambiguate multi-occurrence
        // activities (repeated CoCM followups, cadenced followups) by preferring
        // the LAST completed occurrence. Falling through to the first match would
        // re-target an already-finished earlier occurrence and strand the
        // genuinely-next activity in draft — the multi-followup advancement bug.
        const matchingIndices = carePlan.activity.reduce((acc, activity, index)=>{
            if (resolveActivityCode(activity, tasksById) === completedActivityCode) {
                acc.push(index);
            }
            return acc;
        }, []);
        if (matchingIndices.length > 1) {
            const lastCompletedIndex = [
                ...matchingIndices
            ].reverse().find((index)=>{
                const ref = carePlan.activity?.[index]?.reference?.reference;
                const id = ref?.startsWith("Task/") ? ref.slice(5) : ref;
                return id ? tasksById.get(id)?.status === "completed" : false;
            });
            completedActivityIndex = lastCompletedIndex ?? matchingIndices[0];
        } else {
            completedActivityIndex = matchingIndices[0] ?? -1;
        }
    }
    if (completedActivityIndex === -1) {
        return carePlan;
    }
    if (completionData?.rejectionAction === "restart-previous") {
        const previousActivityIndex = completedActivityIndex - 1;
        if (previousActivityIndex >= 0) {
            const previousActivity = carePlan.activity[previousActivityIndex];
            const previousCode = resolveActivityCode(previousActivity, tasksById);
            if (previousCode) {
                return updateCarePlanActivity(medplum, {
                    carePlan,
                    activityCode: previousCode,
                    taskId: resolveActivityTaskId(previousActivity),
                    newStatus: "ready",
                    statusReason: completionData?.statusReason
                });
            }
        }
        return carePlan;
    }
    // Find the next non-terminal activity. Skip over already completed/cancelled
    // (archived) entries so a stale occurrence sitting between the completed
    // activity and the genuinely-next draft can't stall progression — the
    // archived-followup bug where a newly-added activity never becomes ready.
    let nextActivityIndex = completedActivityIndex + 1;
    while(nextActivityIndex < carePlan.activity.length && isActivityTerminal(carePlan.activity[nextActivityIndex], tasksById)){
        nextActivityIndex += 1;
    }
    if (nextActivityIndex >= carePlan.activity.length) {
        return carePlan;
    }
    const nextActivity = carePlan.activity[nextActivityIndex];
    const nextActivityCode = resolveActivityCode(nextActivity, tasksById);
    if (!nextActivityCode) {
        return carePlan;
    }
    if (isActivityAdvanceable(nextActivity, tasksById)) {
        const ownerOverride = await resolveNextActivityOwner(medplum, nextActivityCode, carePlan, completionData);
        return updateCarePlanActivity(medplum, {
            carePlan,
            activityCode: nextActivityCode,
            taskId: resolveActivityTaskId(nextActivity),
            newStatus: "ready",
            statusReason: completionData?.statusReason,
            dueDateOverride: completionData?.nextActivityDueDate,
            ownerOverride
        });
    }
    return carePlan;
}

/**
 * Cancel an operational care plan with atomic Task updates.
 *
 * 1. Fetches the latest care plan to ensure current activity statuses
 * 2. Fetches linked activity Tasks for Task-backed plans
 * 3. Cancels the care plan (status -> revoked) and open Tasks atomically
 *
 * @agentic @benchmark/core
 */ async function cancelOperationalCarePlan(medplum, input) {
    const { carePlan, reason, planTypeLabel } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required for cancellation");
    }
    let latestCarePlan;
    try {
        latestCarePlan = await medplum.readResource("CarePlan", carePlan.id);
    } catch (error) {
        console.error("Failed to read latest care plan:", error);
        throw new Error(`Failed to read care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    if (latestCarePlan.status === "completed") {
        throw new Error("Cannot cancel a completed care plan");
    }
    if (latestCarePlan.status === "revoked") {
        throw new Error("Care plan is already cancelled");
    }
    const patientId = latestCarePlan.subject?.reference?.replace("Patient/", "") || "";
    const cancelledCarePlanResource = buildCancelledCarePlan(latestCarePlan, reason);
    let activityTasks = [];
    try {
        const result = await getActivityTasks(medplum, carePlan.id);
        if (result) activityTasks = result;
    } catch (error) {
        console.warn("Failed to fetch activity tasks, continuing:", error);
    }
    const openActivityTasks = activityTasks.filter((t)=>!!t.id && t.status !== "completed" && t.status !== "cancelled");
    const entries = [
        {
            resource: cancelledCarePlanResource,
            method: "PUT",
            url: `CarePlan/${cancelledCarePlanResource.id}`
        },
        ...buildCancelledTaskEntries(openActivityTasks, reason || "Care plan cancelled")
    ];
    const bundle = {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
    let result;
    try {
        result = await submitBundle(medplum, bundle);
    } catch (error) {
        console.error("Transaction bundle execution failed:", error);
        throw new Error(`Failed to cancel ${planTypeLabel} care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    let finalCancelledCarePlan;
    try {
        finalCancelledCarePlan = extractSingleResourceFromTransactionResult(result, "CarePlan");
        if (!finalCancelledCarePlan) {
            throw new Error("Care plan not found in transaction response");
        }
    } catch (error) {
        console.error("Failed to extract care plan from transaction:", error);
        throw new Error(`Failed to extract care plan from transaction: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    if (!finalCancelledCarePlan.id) {
        throw new Error("Care plan ID is required after update");
    }
    const event = {
        carePlanId: finalCancelledCarePlan.id,
        carePlan: finalCancelledCarePlan,
        patientId
    };
    globalEventEmitter.publish(DomainEventNames.CarePlanCancelled, event);
    return {
        carePlan: finalCancelledCarePlan
    };
}

/**
 * Create an operational CarePlan with all related resources atomically.
 *
 * - Triage program: one Triage CarePlan + Tasks + ServiceRequests in one
 *   transaction. Cadenced-followup activity is gated by D8 + D10.
 * - CoCM program (no service needs): one CoCM CarePlan + Tasks.
 * - CoCM program with service needs: TWO CarePlans atomically — a CoCM
 *   plan with CoCM-only activities and a sibling Triage plan with the
 *   full triage activity set + the ServiceRequests linked via
 *   `basedOn → Triage plan` (D4).
 * @agentic @benchmark/core
 */ async function createOperationalCarePlan(medplum, input) {
    const { patient, practitionerRole, selectedProgram, serviceRequestDetails, programSelectionObservation, requiresApproval, firstActivityOwnerOverride } = input;
    if (!patient.id) {
        throw new Error("Patient must have an id");
    }
    if (!practitionerRole.id) {
        throw new Error("PractitionerRole must have an id");
    }
    // Step 1: Enforce one active CoCM per patient (D13). Triage plans are
    // permitted alongside any existing active plan (D2 — additive semantics).
    const existingPlans = await getActiveOperationalCarePlans(medplum, patient.id);
    if (selectedProgram === "cocm") {
        const existingCoCM = existingPlans.filter((p)=>p.category?.[0]?.coding?.[0]?.code === "cocm");
        if (existingCoCM.length > 0) {
            throw new Error("Patient already has an active CoCM care plan. " + "Complete or cancel the existing plan before starting a new one.");
        }
    }
    // Step 2: Resolve CareTeam for the patient.
    const careTeams = await medplum.searchResources("CareTeam", {
        subject: `Patient/${patient.id}`,
        status: "active"
    });
    const benchmarkCareTeam = careTeams.find((ct)=>ct.name === "Benchmark Care Team" || ct.category?.[0]?.coding?.[0]?.code === "LA28865-6");
    const careTeamId = benchmarkCareTeam?.id;
    const hasServiceNeeds = Boolean(serviceRequestDetails?.length);
    // Step 3: Resolve cadenced-followup gate (D8 + D10) for any path that may
    // include a Triage plan (standalone Triage, or CoCM-with-referrals).
    const triageInvolved = selectedProgram === "triage" || selectedProgram === "cocm" && hasServiceNeeds;
    let excludeCadencedFollowup = false;
    if (triageInvolved) {
        try {
            excludeCadencedFollowup = await hasGeneratedCadencedFollowups(medplum, patient.id);
        } catch (error) {
            console.warn("Failed to check cadenced-followup history; including activity by default:", error);
        }
    }
    // Step 4: Resolve per-activity owner overrides for the primary plan.
    const baseActivityCodes = getSequentialActivitiesForType(selectedProgram).map((a)=>a.code);
    // For CoCM-with-referrals, add triage activity codes to the resolution
    // list so the sibling Triage plan inherits the same owner waterfall.
    const triageResolutionCodes = triageInvolved && selectedProgram === "cocm" ? [
        "select-services",
        "offer-services",
        "confirm-services",
        "schedule-services",
        "follow-up-on-services",
        ...excludeCadencedFollowup ? [] : [
            "complete-cadenced-followup"
        ]
    ] : [];
    const allActivityCodesToResolve = [
        ...baseActivityCodes,
        ...triageResolutionCodes,
        ...requiresApproval ? [
            "approve-selected-services"
        ] : []
    ];
    const resolvedOverrides = await resolveOwnerOverridesForPlan(medplum, {
        activityCodes: allActivityCodesToResolve,
        patient,
        currentUserRole: practitionerRole
    });
    const firstActivityCode = baseActivityCodes[0];
    if (firstActivityCode && firstActivityOwnerOverride) {
        resolvedOverrides[firstActivityCode] = firstActivityOwnerOverride;
    }
    const taskOwnerOverrides = Object.keys(resolvedOverrides).length > 0 ? resolvedOverrides : undefined;
    const authorDisplay = practitionerRole.practitioner?.display || "Unknown Practitioner";
    // Step 5: Build the primary plan bundle.
    // - Triage primary: include service requests on this plan, gate cadenced.
    // - CoCM primary: NEVER include triage activities or service requests
    //   (those go on the sibling Triage plan in dual-plan path).
    const primaryServiceRequests = selectedProgram === "triage" && hasServiceNeeds ? serviceRequestDetails : undefined;
    const primaryExcludeCodes = selectedProgram === "triage" && excludeCadencedFollowup ? [
        "complete-cadenced-followup"
    ] : undefined;
    const primaryBundle = buildCarePlanBundle({
        type: selectedProgram,
        patientRef: patient.id,
        patientDisplay: getPatientDisplayName(patient),
        authorRef: practitionerRole.id,
        authorDisplay,
        careTeamId,
        initialStatus: "active",
        startFirstActivity: true,
        includeApproval: selectedProgram === "triage" ? requiresApproval : false
    }, undefined, primaryServiceRequests, taskOwnerOverrides, primaryExcludeCodes ? {
        excludeActivityCodes: primaryExcludeCodes
    } : undefined);
    let mergedEntries = primaryBundle.entry ?? [];
    // Step 6: For CoCM-with-referrals, append a sibling Triage bundle in the
    // same transaction (D4). The Triage plan inherits the same owner
    // overrides and the cadenced-followup gate.
    const dualPlan = selectedProgram === "cocm" && hasServiceNeeds;
    if (dualPlan) {
        const triageBundle = buildCarePlanBundle({
            type: "triage",
            patientRef: patient.id,
            patientDisplay: getPatientDisplayName(patient),
            authorRef: practitionerRole.id,
            authorDisplay,
            careTeamId,
            initialStatus: "active",
            startFirstActivity: true,
            includeApproval: requiresApproval
        }, undefined, serviceRequestDetails, taskOwnerOverrides, excludeCadencedFollowup ? {
            excludeActivityCodes: [
                "complete-cadenced-followup"
            ]
        } : undefined);
        mergedEntries = [
            ...mergedEntries,
            ...triageBundle.entry ?? []
        ];
    }
    const result = await submitBundle(medplum, {
        resourceType: "Bundle",
        type: "transaction",
        entry: mergedEntries
    });
    // Step 7: Extract the created CarePlans (and identify CoCM vs Triage).
    const createdCarePlans = extractResourcesFromTransactionResult(result, "CarePlan");
    let primaryCarePlan = createdCarePlans.find((cp)=>cp.category?.[0]?.coding?.[0]?.code === selectedProgram);
    if (!primaryCarePlan) {
        primaryCarePlan = await fallbackReadFirstCarePlan(medplum, result);
    }
    if (!primaryCarePlan?.id) {
        throw new Error("Failed to get ID for created operational CarePlan");
    }
    const siblingTriageCarePlan = dualPlan ? createdCarePlans.find((cp)=>cp.category?.[0]?.coding?.[0]?.code === "triage") : undefined;
    if (dualPlan && !siblingTriageCarePlan) {
        throw new Error("Sibling Triage CarePlan not found in transaction response");
    }
    const createdServiceRequests = extractResourcesFromTransactionResult(result, "ServiceRequest");
    // Step 8: Emit events for each created plan and link the observation.
    globalEventEmitter.publish(DomainEventNames.CarePlanCreated, {
        carePlanId: primaryCarePlan.id,
        carePlan: primaryCarePlan,
        patientId: patient.id
    });
    if (siblingTriageCarePlan?.id) {
        globalEventEmitter.publish(DomainEventNames.CarePlanCreated, {
            carePlanId: siblingTriageCarePlan.id,
            carePlan: siblingTriageCarePlan,
            patientId: patient.id
        });
    }
    // Per D4: when both plans are created, the Program Selection
    // Observation links to the CoCM plan (the program decision). For
    // standalone Triage, it links to the Triage plan as before.
    if (programSelectionObservation?.id) {
        try {
            await updateResource(medplum, {
                ...programSelectionObservation,
                id: programSelectionObservation.id,
                derivedFrom: [
                    {
                        reference: `CarePlan/${primaryCarePlan.id}`,
                        display: primaryCarePlan.title || `${selectedProgram} Care Plan`
                    }
                ]
            });
        } catch (error) {
            // Fire-and-forget per the existing pattern: plans are persisted; the
            // soft FHIR linkage is recoverable.
            console.warn("Failed to link Program Selection Observation:", error);
        }
    }
    return {
        operationalCarePlan: primaryCarePlan,
        siblingTriageCarePlan,
        createdServiceRequests
    };
}
/**
 * Fallback read used when transaction-response entries omit `resource`.
 * @agentic @benchmark/core
 */ async function fallbackReadFirstCarePlan(medplum, result) {
    const locationEntry = result.entry?.find((e)=>e.response?.location?.startsWith("CarePlan/"));
    if (locationEntry?.response?.location) {
        const carePlanId = locationEntry.response.location.split("/")[1];
        return medplum.readResource("CarePlan", carePlanId);
    }
    return undefined;
}

/**
 * Create a patient with all related resources atomically via transaction bundle.
 * @agentic @benchmark/core
 */ async function createPatientWithRelatedResources(medplum, formData, authorReference) {
    // Step 1: Build transaction bundle
    const bundle = buildCreatePatientBundle({
        formData,
        authorReference
    });
    // Step 2: Submit bundle atomically
    const result = await submitBundle(medplum, bundle);
    // Step 3: Extract patient from result
    const patient = extractSingleResourceFromTransactionResult(result, "Patient");
    if (!patient.id) {
        throw new Error("Patient ID is required after creation");
    }
    // Step 4: Emit events for all created resources
    const patientEvent = {
        patientId: patient.id,
        patient
    };
    globalEventEmitter.publish(DomainEventNames.PatientCreated, patientEvent);
    // Extract and emit events for Coverages
    const coverages = extractResourcesFromTransactionResult(result, "Coverage");
    for (const coverage of coverages){
        if (coverage.id) {
            const coverageEvent = {
                coverageId: coverage.id,
                coverage,
                patientId: patient.id
            };
            globalEventEmitter.publish(DomainEventNames.CoverageCreated, coverageEvent);
        }
    }
    // Extract and emit events for RelatedPersons
    const relatedPersons = extractResourcesFromTransactionResult(result, "RelatedPerson");
    for (const relatedPerson of relatedPersons){
        if (relatedPerson.id) {
            const relatedPersonEvent = {
                relatedPersonId: relatedPerson.id,
                relatedPerson,
                patientId: patient.id
            };
            globalEventEmitter.publish(DomainEventNames.RelatedPersonCreated, relatedPersonEvent);
        }
    }
    // Extract and emit event for CareTeam
    const careTeams = extractResourcesFromTransactionResult(result, "CareTeam");
    for (const careTeam of careTeams){
        if (careTeam.id) {
            const careTeamEvent = {
                careTeamId: careTeam.id,
                careTeam,
                patientId: patient.id
            };
            globalEventEmitter.publish(DomainEventNames.CareTeamCreated, careTeamEvent);
        }
    }
    // Extract and emit event for ServiceRequest
    const serviceRequests = extractResourcesFromTransactionResult(result, "ServiceRequest");
    for (const serviceRequest of serviceRequests){
        if (serviceRequest.id) {
            const serviceRequestEvent = {
                serviceRequestId: serviceRequest.id,
                serviceRequest,
                patientId: patient.id
            };
            globalEventEmitter.publish(DomainEventNames.ServiceRequestCreated, serviceRequestEvent);
        }
    }
    // Extract and emit events for Conditions (ICD-10 Problem List Items)
    const conditions = extractResourcesFromTransactionResult(result, "Condition");
    for (const condition of conditions){
        if (condition.id) {
            const conditionEvent = {
                conditionId: condition.id,
                condition,
                patientId: patient.id
            };
            globalEventEmitter.publish(DomainEventNames.ConditionCreated, conditionEvent);
        }
    }
    // Extract and emit event for Task
    const tasks = extractResourcesFromTransactionResult(result, "Task");
    for (const task of tasks){
        if (task.id) {
            const taskEvent = {
                taskId: task.id,
                task,
                patientId: patient.id
            };
            globalEventEmitter.publish(DomainEventNames.TaskCreated, taskEvent);
        }
    }
    return patient;
}

/**
 * Handle CoCM care plan cancellation via transaction bundle. Deactivates flag and updates engagement status.
 * @agentic @benchmark/core
 */ async function handleCoCMCancellation(medplum, input) {
    const { carePlan, reason } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required for cancellation");
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "");
    if (!patientId) {
        throw new Error("Patient ID is required for CoCM cancellation");
    }
    // Step 1: Get patient resource and search for CoCM flag
    let patient;
    try {
        patient = await medplum.readResource("Patient", patientId);
    } catch (error) {
        console.error("Failed to read patient resource:", error);
        throw new Error(`Failed to read patient: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // If flag search fails, continue without flag (will skip deactivation)
    let existingFlag = null;
    try {
        existingFlag = await findExistingCoCMFlag(medplum, patientId);
    } catch (error) {
        console.warn("Failed to search for CoCM flag, will skip deactivation:", error);
    // Continue - flag deactivation is optional
    }
    // Step 2: Fetch latest care plan to ensure current activity statuses
    let latestCarePlan;
    try {
        latestCarePlan = await medplum.readResource("CarePlan", carePlan.id);
    } catch (error) {
        console.error("Failed to read latest care plan:", error);
        throw new Error(`Failed to read care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    if (latestCarePlan.status === "completed") {
        throw new Error("Cannot cancel a completed care plan");
    }
    if (latestCarePlan.status === "revoked") {
        throw new Error("Care plan is already cancelled");
    }
    // Step 3: Build cancelled care plan using action helper
    const cancelledCarePlan = buildCancelledCarePlan(latestCarePlan, reason);
    // Step 4: Get all care plans to determine engagement status
    const allCarePlans = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patientId}`
    });
    // Step 5: Determine new engagement status
    const currentStatus = getPatientEngagementStatus$1(patient);
    const suggestedStatus = resolveEngagementStatusFromCarePlans(allCarePlans, currentStatus?.code);
    // Step 6: Fetch open tasks if setting terminating engagement status
    const tasksToCancel = suggestedStatus && isTerminatingEngagementStatus(suggestedStatus) && patient.id ? await findOpenTasksForPatient(medplum, patient.id) : [];
    // Step 6b: Fetch activity Tasks for atomic cancellation
    let activityTasks = [];
    try {
        const result = await getActivityTasks(medplum, carePlan.id);
        if (result) activityTasks = result;
    } catch (error) {
        console.warn("Failed to fetch activity tasks, continuing:", error);
    }
    const openActivityTasks = activityTasks.filter((t)=>!!t.id && t.status !== "completed" && t.status !== "cancelled");
    // Merge activity tasks with standalone tasks to cancel (deduplicate by ID)
    const allTaskIds = new Set(tasksToCancel.map((t)=>t.id));
    const additionalActivityTasks = openActivityTasks.filter((t)=>!allTaskIds.has(t.id));
    const combinedTasksToCancel = [
        ...tasksToCancel,
        ...additionalActivityTasks
    ];
    // Step 7: Build transaction bundle
    // Only include flag if it exists and is active
    const flagToDeactivate = existingFlag && existingFlag.status === "active" ? existingFlag : null;
    const bundle = buildCoCMCancellationBundle({
        cancelledCarePlan,
        flagToDeactivate,
        patient,
        newEngagementStatus: suggestedStatus || undefined,
        tasksToCancel: combinedTasksToCancel
    });
    // Step 8: Execute transaction atomically
    let result;
    try {
        result = await submitBundle(medplum, bundle);
    } catch (error) {
        console.error("Transaction bundle execution failed:", error);
        throw new Error(`Failed to cancel CoCM care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // Step 9: Extract results from transaction response
    let finalCarePlan;
    try {
        finalCarePlan = extractSingleResourceFromTransactionResult(result, "CarePlan");
        if (!finalCarePlan) {
            throw new Error("Care plan not found in transaction response");
        }
    } catch (error) {
        console.error("Failed to extract care plan from transaction:", error);
        throw new Error(`Failed to extract care plan from transaction: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    const flags = extractResourcesFromTransactionResult(result, "Flag");
    const deactivatedFlag = flags.length > 0 ? flags[0] : null;
    const flagDeactivated = flagToDeactivate !== null && deactivatedFlag !== null;
    const patients = extractResourcesFromTransactionResult(result, "Patient");
    const updatedPatient = patients.length > 0 ? patients[0] : null;
    const engagementStatusUpdated = suggestedStatus !== null && updatedPatient !== null;
    // Step 10: Emit events
    if (!finalCarePlan.id || !patient.id) {
        throw new Error("Missing required IDs for events");
    }
    const carePlanEvent = {
        carePlanId: finalCarePlan.id,
        carePlan: finalCarePlan,
        patientId: patient.id
    };
    globalEventEmitter.publish(DomainEventNames.CarePlanCancelled, carePlanEvent);
    if (deactivatedFlag?.id) {
        const flagEvent = {
            flagId: deactivatedFlag.id,
            flag: deactivatedFlag,
            patientId: patient.id
        };
        globalEventEmitter.publish(DomainEventNames.FlagUpdated, flagEvent);
    }
    if (updatedPatient?.id && engagementStatusUpdated) {
        const patientEvent = {
            patientId: updatedPatient.id,
            patient: updatedPatient
        };
        globalEventEmitter.publish(DomainEventNames.PatientUpdated, patientEvent);
    }
    // Activity Tasks cancelled atomically in the transaction Bundle above
    return {
        carePlan: finalCarePlan,
        flagDeactivated,
        engagementStatusUpdated,
        newEngagementStatus: suggestedStatus || undefined
    };
}

/**
 * Enroll a patient in Collaborative Care Management (CoCM).
 *
 * - Validates patient eligibility (no other active CoCM plan).
 * - Revokes any prior CoCM CarePlans (D13: single-active-CoCM rule).
 * - Builds the CoCM enrollment bundle (CarePlan + Tasks + Flag + Patient).
 * - When `serviceRequestDetails` is provided, ALSO builds a sibling Triage
 *   CarePlan + Tasks + ServiceRequests bundle and concatenates the entries
 *   into the same transaction (D4). ServiceRequests get
 *   `basedOn → Triage plan`; the Program Selection Observation linkage is
 *   handled by the caller (`useProgramSelection` / intake-completion).
 * - Atomicity is provided by the underlying FHIR transaction.
 *
 * @agentic @benchmark/core
 */ async function enrollInCoCM(medplum, input) {
    const { patient, practitionerRole, careTeamId, consentDate, periodStart, notes, serviceRequestDetails, requiresApproval } = input;
    if (!patient.id) {
        throw new Error("Patient must have an id for CoCM enrollment");
    }
    // Step 1: Validate patient eligibility (cross-category constraints only;
    // same-category prior plans are revoked below per D13).
    const existingCarePlans = await getActiveCarePlans(medplum, patient.id);
    const validation = validateCoCMCarePlanCreation(existingCarePlans.filter((carePlan)=>carePlan.category?.[0]?.coding?.[0]?.code !== "cocm"));
    if (!validation.isValid) {
        throw new Error(`Cannot enroll in CoCM: ${validation.errors.join(", ")}`);
    }
    // Step 1.5: Revoke any active/draft CoCM plan and collect replaces linkage
    // (single-active-CoCM rule preserved; D2/D3 leave Triage siblings alone).
    const sameCategoryPlans = existingCarePlans.filter((carePlan)=>!!carePlan.id && carePlan.category?.[0]?.coding?.[0]?.code === "cocm" && (carePlan.status === "active" || carePlan.status === "draft"));
    const replacedCarePlansMap = new Map();
    for (const existingPlan of sameCategoryPlans){
        const { carePlan: revokedCarePlan } = await handleCoCMCancellation(medplum, {
            carePlan: existingPlan,
            reason: "Superseded by a new CoCM enrollment"
        });
        if (!revokedCarePlan.id) {
            continue;
        }
        replacedCarePlansMap.set(revokedCarePlan.id, {
            id: revokedCarePlan.id,
            display: revokedCarePlan.title || `${getCarePlanCategoryDisplay("cocm")} Care Plan`
        });
    }
    // Include historical CoCM plans (revoked/completed/etc.) in replaces
    // linkage (preserves provenance even after the new plan is created).
    const allCarePlans = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patient.id}`,
        _sort: "-date",
        _count: 100
    });
    for (const carePlan of allCarePlans){
        if (!carePlan.id) {
            continue;
        }
        if (carePlan.category?.[0]?.coding?.[0]?.code !== "cocm") {
            continue;
        }
        if (replacedCarePlansMap.has(carePlan.id)) {
            continue;
        }
        replacedCarePlansMap.set(carePlan.id, {
            id: carePlan.id,
            display: carePlan.title || `${getCarePlanCategoryDisplay("cocm")} Care Plan`
        });
    }
    const replacesCarePlans = Array.from(replacedCarePlansMap.values());
    // Step 2: Search for existing CoCM flag.
    let existingFlag = null;
    try {
        existingFlag = await findExistingCoCMFlag(medplum, patient.id);
    } catch (error) {
        console.warn("Failed to search for existing CoCM flag, will create new one:", error);
    }
    // Step 3: Resolve CareTeam if not provided.
    let resolvedCareTeamId = careTeamId;
    if (!resolvedCareTeamId) {
        const careTeams = await medplum.searchResources("CareTeam", {
            subject: `Patient/${patient.id}`,
            status: "active"
        });
        const benchmarkCareTeam = careTeams.find((ct)=>ct.name === "Benchmark Care Team" || ct.category?.[0]?.coding?.[0]?.code === "LA28865-6");
        resolvedCareTeamId = benchmarkCareTeam?.id;
    }
    // Step 4: Fetch recent assessments for supportingInfo.
    const recentAssessments = await medplum.searchResources("QuestionnaireResponse", {
        subject: `Patient/${patient.id}`,
        _sort: "-authored",
        _count: 3
    });
    const supportingInfo = (recentAssessments?.length ?? 0) > 0 ? recentAssessments.map((qr)=>({
            reference: `QuestionnaireResponse/${qr.id}`,
            display: qr.questionnaire?.split("/").pop() || "Assessment"
        })) : undefined;
    // Step 5: Convert periodStart to datetime.
    const periodStartDateTime = periodStart ? /^\d{4}-\d{2}-\d{2}$/.test(periodStart) ? `${periodStart}T12:00:00Z` : periodStart : new Date().toISOString();
    // Step 6: Check if engagement status needs updating.
    const currentStatus = getPatientEngagementStatus$1(patient);
    const shouldUpdateEngagementStatus = sameCategoryPlans.length > 0 || currentStatus?.code !== "engaged";
    const hasServiceNeeds = Boolean(serviceRequestDetails?.length);
    // Step 7: Build the CoCM bundle (CarePlan + Tasks + Flag + Patient).
    const cocmBundle = buildCoCMEnrollmentBundle({
        patient: patient,
        practitionerRole,
        careTeamId: resolvedCareTeamId,
        periodStart: periodStartDateTime,
        consentDate,
        notes,
        supportingInfo,
        existingFlag,
        shouldUpdateEngagementStatus,
        ...replacesCarePlans.length > 0 ? {
            replaces: replacesCarePlans.map((plan)=>({
                    reference: `CarePlan/${plan.id}`,
                    display: plan.display
                }))
        } : {}
    });
    let mergedEntries = cocmBundle.entry ?? [];
    // Step 8: When service needs are present, build a sibling Triage bundle
    // and concatenate its entries (atomic dual-plan creation per D4).
    let triageRequested = false;
    if (hasServiceNeeds) {
        triageRequested = true;
        const authorDisplay = practitionerRole.practitioner?.display || "Unknown Practitioner";
        let excludeCadenced = false;
        try {
            excludeCadenced = await hasGeneratedCadencedFollowups(medplum, patient.id);
        } catch (error) {
            console.warn("Failed to check cadenced-followup history; including activity by default:", error);
        }
        const triageActivityCodes = [
            "select-services",
            ...requiresApproval ? [
                "approve-selected-services"
            ] : [],
            "offer-services",
            "confirm-services",
            "schedule-services",
            "follow-up-on-services",
            ...excludeCadenced ? [] : [
                "complete-cadenced-followup"
            ]
        ];
        const triageOwnerOverrides = await resolveOwnerOverridesForPlan(medplum, {
            activityCodes: triageActivityCodes,
            patient,
            currentUserRole: practitionerRole
        });
        const triageBundle = buildCarePlanBundle({
            type: "triage",
            patientRef: patient.id,
            patientDisplay: getPatientDisplayName(patient),
            authorRef: practitionerRole.id,
            authorDisplay,
            careTeamId: resolvedCareTeamId,
            periodStart: periodStartDateTime,
            includeApproval: requiresApproval,
            initialStatus: "active",
            startFirstActivity: true
        }, undefined, serviceRequestDetails, Object.keys(triageOwnerOverrides).length > 0 ? triageOwnerOverrides : undefined, excludeCadenced ? {
            excludeActivityCodes: [
                "complete-cadenced-followup"
            ]
        } : undefined);
        mergedEntries = [
            ...mergedEntries,
            ...triageBundle.entry ?? []
        ];
    }
    // Step 9: Submit the merged transaction atomically.
    let result;
    try {
        result = await submitBundle(medplum, {
            resourceType: "Bundle",
            type: "transaction",
            entry: mergedEntries
        });
    } catch (error) {
        console.error("Transaction bundle execution failed:", error);
        throw new Error(`Failed to enroll patient in CoCM: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // Step 10: Extract created resources from the transaction response.
    const createdCarePlans = extractResourcesFromTransactionResult(result, "CarePlan");
    const createdCoCMCarePlan = createdCarePlans.find((cp)=>cp.category?.[0]?.coding?.[0]?.code === "cocm");
    const createdTriageCarePlan = createdCarePlans.find((cp)=>cp.category?.[0]?.coding?.[0]?.code === "triage");
    if (!createdCoCMCarePlan) {
        throw new Error("CoCM care plan not found in transaction response");
    }
    if (triageRequested && !createdTriageCarePlan) {
        throw new Error("Sibling Triage care plan not found in transaction response");
    }
    const flags = extractResourcesFromTransactionResult(result, "Flag");
    const createdFlag = flags.length > 0 ? flags[0] : null;
    const flagCreated = createdFlag !== null;
    // Step 11: Emit events.
    if (!createdCoCMCarePlan.id || !patient.id) {
        throw new Error("Missing required IDs for events");
    }
    const cocmCreatedEvent = {
        carePlanId: createdCoCMCarePlan.id,
        carePlan: createdCoCMCarePlan,
        patientId: patient.id
    };
    globalEventEmitter.publish(DomainEventNames.CarePlanCreated, cocmCreatedEvent);
    if (createdTriageCarePlan?.id) {
        const triageCreatedEvent = {
            carePlanId: createdTriageCarePlan.id,
            carePlan: createdTriageCarePlan,
            patientId: patient.id
        };
        globalEventEmitter.publish(DomainEventNames.CarePlanCreated, triageCreatedEvent);
    }
    if (createdFlag?.id) {
        if (existingFlag?.id) {
            const flagUpdatedEvent = {
                flagId: createdFlag.id,
                flag: createdFlag,
                patientId: patient.id
            };
            globalEventEmitter.publish(DomainEventNames.FlagUpdated, flagUpdatedEvent);
        } else {
            const flagCreatedEvent = {
                flagId: createdFlag.id,
                flag: createdFlag,
                patientId: patient.id
            };
            globalEventEmitter.publish(DomainEventNames.FlagCreated, flagCreatedEvent);
        }
    }
    // A Patient PUT is present in the transaction whenever engagement status or
    // CoCM consent was written back (see buildCoCMEnrollmentBundle). Emit
    // PatientUpdated whenever the transaction returned a Patient so consent-only
    // write-backs also refresh patient-consuming views (mutation-refetch rule).
    const patients = extractResourcesFromTransactionResult(result, "Patient");
    const updatedPatient = patients.length > 0 ? patients[0] : null;
    if (updatedPatient?.id) {
        const patientUpdatedEvent = {
            patientId: updatedPatient.id,
            patient: updatedPatient
        };
        globalEventEmitter.publish(DomainEventNames.PatientUpdated, patientUpdatedEvent);
    }
    return {
        carePlan: createdCoCMCarePlan,
        triageCarePlan: createdTriageCarePlan,
        flagCreated,
        engagementStatusUpdated: shouldUpdateEngagementStatus
    };
}

/**
 * Handle intake care plan cancellation. Updates engagement status to referred when needed.
 * @agentic @benchmark/core
 */ async function handleIntakeCancellation(medplum, input) {
    const { carePlan, reason } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required for cancellation");
    }
    // Fetch latest care plan to ensure current activity statuses
    let latestCarePlan;
    try {
        latestCarePlan = await medplum.readResource("CarePlan", carePlan.id);
    } catch (error) {
        console.error("Failed to read latest care plan:", error);
        throw new Error(`Failed to read care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    if (latestCarePlan.status === "completed") {
        throw new Error("Cannot cancel a completed care plan");
    }
    if (latestCarePlan.status === "revoked") {
        throw new Error("Care plan is already cancelled");
    }
    const patientId = latestCarePlan.subject?.reference?.replace("Patient/", "") || "";
    if (!patientId) {
        throw new Error("Patient ID is required for intake cancellation");
    }
    // Step 2: Get patient and check engagement status
    let patient;
    try {
        patient = await medplum.readResource("Patient", patientId);
    } catch (error) {
        console.error("Failed to read patient resource:", error);
        throw new Error(`Failed to read patient: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    const currentStatus = getPatientEngagementStatus$1(patient);
    const shouldUpdateEngagementStatus = currentStatus?.code === "engaged";
    // Step 3: Build cancelled care plan using action helper
    const cancelledCarePlanResource = buildCancelledCarePlan(latestCarePlan, reason);
    // Step 3b: Fetch activity Tasks for atomic cancellation
    let activityTasks = [];
    try {
        const result = await getActivityTasks(medplum, carePlan.id);
        if (result) activityTasks = result;
    } catch (error) {
        console.warn("Failed to fetch activity tasks, continuing:", error);
    }
    const openActivityTasks = activityTasks.filter((t)=>!!t.id && t.status !== "completed" && t.status !== "cancelled");
    // Step 4: Build transaction bundle (CarePlan + Patient + Tasks)
    const bundle = buildIntakeCancellationBundle({
        cancelledCarePlan: cancelledCarePlanResource,
        patient,
        updateEngagementStatus: shouldUpdateEngagementStatus,
        activityTasksToCancel: openActivityTasks,
        cancellationReason: reason
    });
    // Step 5: Execute transaction atomically
    let result;
    try {
        result = await submitBundle(medplum, bundle);
    } catch (error) {
        console.error("Transaction bundle execution failed:", error);
        throw new Error(`Failed to cancel intake care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // Step 6: Extract results from transaction response
    let finalCarePlan;
    try {
        finalCarePlan = extractSingleResourceFromTransactionResult(result, "CarePlan");
        if (!finalCarePlan) {
            throw new Error("Care plan not found in transaction response");
        }
    } catch (error) {
        console.error("Failed to extract care plan from transaction:", error);
        throw new Error(`Failed to extract care plan from transaction: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    const patients = extractResourcesFromTransactionResult(result, "Patient");
    const updatedPatient = patients.length > 0 ? patients[0] : null;
    const engagementStatusUpdated = shouldUpdateEngagementStatus && updatedPatient !== null;
    // Step 7: Emit events (activity Tasks cancelled atomically in transaction)
    if (!finalCarePlan.id || !patient.id) {
        throw new Error("Missing required IDs for events");
    }
    const carePlanEvent = {
        carePlanId: finalCarePlan.id,
        carePlan: finalCarePlan,
        patientId: patient.id
    };
    globalEventEmitter.publish(DomainEventNames.CarePlanCancelled, carePlanEvent);
    if (updatedPatient?.id && engagementStatusUpdated) {
        const patientEvent = {
            patientId: updatedPatient.id,
            patient: updatedPatient
        };
        globalEventEmitter.publish(DomainEventNames.PatientUpdated, patientEvent);
    }
    return {
        carePlan: finalCarePlan,
        engagementStatusUpdated
    };
}

/**
 * Enroll a patient in the Intake care plan. Sets engagement status and advances first activity.
 * @agentic @benchmark/core
 */ async function enrollInIntake(medplum, input) {
    const { patient, practitionerRole, careTeamId, periodStart, notes } = input;
    if (!patient.id) {
        throw new Error("Patient must have an id for intake enrollment");
    }
    // Align engagement status if it is missing
    let engagementStatusUpdated = false;
    try {
        const currentStatus = getPatientEngagementStatus$1(patient);
        if (!currentStatus) {
            await updatePatientEngagementStatus(medplum, patient, "referred");
            engagementStatusUpdated = true;
        }
    } catch (error) {
        // Engagement status is a side effect, so log and continue
        console.error("Failed to update engagement status:", error);
    }
    // Revoke existing active/draft intake plans before creating the replacement plan.
    const existingCarePlans = await getActiveCarePlans(medplum, patient.id);
    const sameCategoryPlans = existingCarePlans.filter((carePlan)=>!!carePlan.id && carePlan.category?.[0]?.coding?.[0]?.code === "intake" && (carePlan.status === "active" || carePlan.status === "draft"));
    const replacedCarePlansMap = new Map();
    for (const existingPlan of sameCategoryPlans){
        const { carePlan: revokedCarePlan } = await handleIntakeCancellation(medplum, {
            carePlan: existingPlan,
            reason: "Superseded by a new Intake enrollment"
        });
        if (!revokedCarePlan.id) {
            continue;
        }
        replacedCarePlansMap.set(revokedCarePlan.id, {
            id: revokedCarePlan.id,
            display: revokedCarePlan.title || `${getCarePlanCategoryDisplay("intake")} Care Plan`
        });
    }
    // Include historical intake plans (revoked/completed/etc.) in replaces linkage.
    const allCarePlans = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patient.id}`,
        _sort: "-date",
        _count: 100
    });
    for (const carePlan of allCarePlans){
        if (!carePlan.id) {
            continue;
        }
        if (carePlan.category?.[0]?.coding?.[0]?.code !== "intake") {
            continue;
        }
        if (replacedCarePlansMap.has(carePlan.id)) {
            continue;
        }
        replacedCarePlansMap.set(carePlan.id, {
            id: carePlan.id,
            display: carePlan.title || `${getCarePlanCategoryDisplay("intake")} Care Plan`
        });
    }
    const replacesCarePlans = Array.from(replacedCarePlansMap.values());
    // Create intake care plan with immediate activation
    const intakeCarePlan = await createCarePlan(medplum, {
        patient,
        type: "intake",
        practitionerRole,
        careTeamId,
        periodStart,
        notes,
        ...replacesCarePlans.length > 0 ? {
            replacesCarePlans
        } : {}
    });
    // Mirror onboarding behavior by advancing the first activity automatically
    const carePlanWithFirstActivity = await advanceFirstActivityToReady(medplum, intakeCarePlan);
    return {
        carePlan: carePlanWithFirstActivity,
        engagementStatusUpdated
    };
}

/**
 * Enroll a patient in triage care coordination. Always additive: per the
 * multi-plan model (D2), an existing active Triage plan is never revoked when
 * enrolling a new one — both coexist until an operator explicitly cancels
 * one. The new plan also honors the once-per-patient-lifetime
 * cadenced-followup rule (D8 + D10): if any historical
 * `complete-cadenced-followup` Task exists for this patient (any status), the
 * new plan omits that activity.
 * @agentic @benchmark/core
 */ async function enrollInTriage(medplum, input) {
    const { patient, practitionerRole, careTeamId, periodStart, notes, serviceRequestDetails, requiresApproval } = input;
    if (!patient.id) {
        throw new Error("Patient must have an id for triage enrollment");
    }
    // Step 1: Validate patient eligibility (cross-category constraints only).
    // Per D2 the same-category constraint is removed: a patient may hold
    // many active Triage plans simultaneously.
    const existingCarePlans = await getActiveCarePlans(medplum, patient.id);
    const validation = validateTriageCarePlanCreation(existingCarePlans.filter((carePlan)=>carePlan.category?.[0]?.coding?.[0]?.code !== "triage"));
    if (!validation.isValid) {
        throw new Error(`Cannot enroll in triage: ${validation.errors.join(", ")}`);
    }
    // Step 2: Resolve cadenced-followup gate (D8 + D10). Fail-soft: if the
    // search throws, default to including the activity (rare false-include
    // beats blocking enrollment on a transient FHIR-server hiccup).
    let excludeCadencedFollowup = false;
    try {
        excludeCadencedFollowup = await hasGeneratedCadencedFollowups(medplum, patient.id);
    } catch (error) {
        console.warn("Failed to check cadenced-followup history; including activity by default:", error);
    }
    // Step 3: Create triage care plan via action (creates in active status).
    // Note: no `replacesCarePlans` linkage — multi-plan plans don't replace
    // each other.
    const carePlan = await createCarePlan(medplum, {
        patient,
        type: "triage",
        practitionerRole,
        careTeamId,
        periodStart,
        notes,
        serviceRequestDetails,
        includeApproval: serviceRequestDetails && serviceRequestDetails.length > 0 ? requiresApproval : undefined,
        excludeCadencedFollowup
    });
    // Step 4: Advance first activity to in-progress for immediate workflow
    // visibility.
    let carePlanWithFirstActivity = carePlan;
    try {
        carePlanWithFirstActivity = await advanceFirstActivityToReady(medplum, carePlan);
    } catch (error) {
        console.error("Failed to advance first activity to in-progress:", error);
    }
    // Step 5: Update engagement status to "engaged" if not already.
    let engagementStatusUpdated = false;
    try {
        const currentStatus = getPatientEngagementStatus$1(patient);
        if (currentStatus?.code !== "engaged") {
            await updatePatientEngagementStatus(medplum, patient, "engaged");
            engagementStatusUpdated = true;
        }
    } catch (error) {
        console.error("Failed to update engagement status:", error);
    }
    return {
        carePlan: carePlanWithFirstActivity,
        engagementStatusUpdated
    };
}

/**
 * Gets or creates a CoCM care plan for a patient. Returns the existing CoCM
 * care plan when one exists; otherwise creates a fresh one via
 * `createCarePlan`. Race-condition recovery: if the action throws because a
 * care plan already exists, re-fetch and return it.
 * @agentic @benchmark/core
 */ async function getOrCreateCoCMCarePlan(medplum, patient, practitionerRole) {
    if (!patient.id) {
        throw new Error("Patient must have an id to get or create CoCM care plan");
    }
    const existingCoCMCarePlan = await getCarePlanByType(medplum, patient.id, "cocm");
    if (existingCoCMCarePlan) {
        return existingCoCMCarePlan;
    }
    try {
        return await createCarePlan(medplum, {
            patient,
            type: "cocm",
            practitionerRole
        });
    } catch (error) {
        if (error instanceof Error && error.message.includes("already has an active")) {
            const carePlan = await getCarePlanByType(medplum, patient.id, "cocm");
            if (carePlan) {
                return carePlan;
            }
        }
        throw new Error(`Failed to create CoCM care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
}

/**
 * Handle intake care plan completion via transaction bundle.
 *
 * Program Selection Observation is intentionally ignored for completion
 * eligibility because enrollment proof is the care plan resources themselves.
 * @agentic @benchmark/core
 */ async function handleIntakeCompletion(medplum, input) {
    const { carePlan, force = false, reason } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required for completion");
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "");
    if (!patientId) {
        throw new Error("Patient ID is required for intake completion");
    }
    // Step 1: Fetch latest care plan and patient
    let latestCarePlan;
    let patient;
    try {
        latestCarePlan = await medplum.readResource("CarePlan", carePlan.id);
        patient = await medplum.readResource("Patient", patientId);
    } catch (error) {
        console.error("Failed to read resources:", error);
        throw new Error(`Failed to read resources: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // Validate care plan can be completed
    if (latestCarePlan.status === "completed") {
        throw new Error("Care plan is already completed");
    }
    const tasksById = force ? undefined : await buildCarePlanTasksMap(medplum, latestCarePlan);
    if (!force && !areAllActivitiesCompleted(latestCarePlan, tasksById)) {
        throw new Error("Cannot complete care plan: not all activities are completed. Use force=true to complete anyway.");
    }
    // Step 2: Build completed care plan resource
    const completedCarePlanResource = buildCompletedCarePlan(latestCarePlan, reason);
    // Step 3: Build transaction bundle
    const bundle = buildIntakeCompletionBundle({
        completedCarePlan: completedCarePlanResource,
        patient
    });
    // Step 4: Execute transaction atomically
    let result;
    try {
        result = await submitBundle(medplum, bundle);
    } catch (error) {
        console.error("Transaction bundle execution failed:", error);
        throw new Error(`Failed to complete intake care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // Step 5: Extract results from transaction response
    let finalCarePlan;
    try {
        finalCarePlan = extractSingleResourceFromTransactionResult(result, "CarePlan");
        if (!finalCarePlan) {
            throw new Error("Care plan not found in transaction response");
        }
    } catch (error) {
        console.error("Failed to extract care plan from transaction:", error);
        throw new Error(`Failed to extract care plan from transaction: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    const patients = extractResourcesFromTransactionResult(result, "Patient");
    const updatedPatient = patients.length > 0 ? patients[0] : null;
    const engagementStatusUpdated = updatedPatient !== null;
    // Step 6: Emit events
    if (!finalCarePlan.id || !patient.id) {
        throw new Error("Missing required IDs for events");
    }
    const carePlanEvent = {
        carePlanId: finalCarePlan.id,
        carePlan: finalCarePlan,
        patientId: patient.id
    };
    globalEventEmitter.publish(DomainEventNames.CarePlanCompleted, carePlanEvent);
    if (updatedPatient?.id && engagementStatusUpdated) {
        const patientEvent = {
            patientId: updatedPatient.id,
            patient: updatedPatient
        };
        globalEventEmitter.publish(DomainEventNames.PatientUpdated, patientEvent);
    }
    return {
        carePlan: finalCarePlan,
        engagementStatusUpdated
    };
}

/**
 * Handle triage care plan completion. No special side effects beyond completing the care plan.
 * @agentic @benchmark/core
 */ async function handleTriageCompletion(medplum, input) {
    const { carePlan, force = false, reason } = input;
    // Complete the triage care plan
    const completedCarePlan = await completeCarePlan(medplum, {
        carePlan,
        force,
        reason
    });
    return {
        carePlan: completedCarePlan
    };
}

/**
 * Handle CoCM care plan completion via transaction bundle. Deactivates flag and updates engagement status.
 * @agentic @benchmark/core
 */ async function handleCoCMCompletion(medplum, input) {
    const { carePlan, force: _force = false, reason } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required for completion");
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "");
    if (!patientId) {
        throw new Error("Patient ID is required for CoCM completion");
    }
    // Step 1: Get patient resource and search for CoCM flag
    let patient;
    try {
        patient = await medplum.readResource("Patient", patientId);
    } catch (error) {
        console.error("Failed to read patient resource:", error);
        throw new Error(`Failed to read patient: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // If flag search fails, continue without flag (will skip deactivation)
    let existingFlag = null;
    try {
        existingFlag = await findExistingCoCMFlag(medplum, patientId);
    } catch (error) {
        console.warn("Failed to search for CoCM flag, will skip deactivation:", error);
    // Continue - flag deactivation is optional
    }
    // Step 2: Fetch latest care plan to ensure current activity statuses
    let latestCarePlan;
    try {
        latestCarePlan = await medplum.readResource("CarePlan", carePlan.id);
    } catch (error) {
        console.error("Failed to read latest care plan:", error);
        throw new Error(`Failed to read care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // Step 3: Build completed care plan using action helper
    const completedCarePlan = buildCompletedCarePlan(latestCarePlan, reason);
    // Step 4: Get all care plans to determine engagement status
    const allCarePlans = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patientId}`
    });
    // Step 5: Determine new engagement status
    const currentStatus = getPatientEngagementStatus$1(patient);
    const suggestedStatus = resolveEngagementStatusFromCarePlans(allCarePlans, currentStatus?.code);
    // Step 6: Fetch open tasks if setting terminating engagement status
    const tasksToCancel = suggestedStatus && isTerminatingEngagementStatus(suggestedStatus) && patient.id ? await findOpenTasksForPatient(medplum, patient.id) : [];
    // Step 7: Build transaction bundle
    // Only include flag if it exists and is active
    const flagToDeactivate = existingFlag && existingFlag.status === "active" ? existingFlag : null;
    const bundle = buildCoCMCompletionBundle({
        completedCarePlan,
        flagToDeactivate,
        patient,
        newEngagementStatus: suggestedStatus || undefined,
        tasksToCancel
    });
    // Step 8: Execute transaction atomically
    let result;
    try {
        result = await submitBundle(medplum, bundle);
    } catch (error) {
        console.error("Transaction bundle execution failed:", error);
        throw new Error(`Failed to complete CoCM care plan: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // Step 9: Extract results from transaction response
    let finalCarePlan;
    try {
        finalCarePlan = extractSingleResourceFromTransactionResult(result, "CarePlan");
        if (!finalCarePlan) {
            throw new Error("Care plan not found in transaction response");
        }
    } catch (error) {
        console.error("Failed to extract care plan from transaction:", error);
        throw new Error(`Failed to extract care plan from transaction: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    const flags = extractResourcesFromTransactionResult(result, "Flag");
    const deactivatedFlag = flags.length > 0 ? flags[0] : null;
    const flagDeactivated = flagToDeactivate !== null && deactivatedFlag !== null;
    const patients = extractResourcesFromTransactionResult(result, "Patient");
    const updatedPatient = patients.length > 0 ? patients[0] : null;
    const engagementStatusUpdated = suggestedStatus !== null && updatedPatient !== null;
    // Step 10: Emit events
    if (!finalCarePlan.id || !patient.id) {
        throw new Error("Missing required IDs for events");
    }
    const carePlanEvent = {
        carePlanId: finalCarePlan.id,
        carePlan: finalCarePlan,
        patientId: patient.id
    };
    globalEventEmitter.publish(DomainEventNames.CarePlanCompleted, carePlanEvent);
    if (deactivatedFlag?.id) {
        const flagEvent = {
            flagId: deactivatedFlag.id,
            flag: deactivatedFlag,
            patientId: patient.id
        };
        globalEventEmitter.publish(DomainEventNames.FlagUpdated, flagEvent);
    }
    if (updatedPatient?.id && engagementStatusUpdated) {
        const patientEvent = {
            patientId: updatedPatient.id,
            patient: updatedPatient
        };
        globalEventEmitter.publish(DomainEventNames.PatientUpdated, patientEvent);
    }
    return {
        carePlan: finalCarePlan,
        flagDeactivated,
        engagementStatusUpdated,
        newEngagementStatus: suggestedStatus || undefined
    };
}

/**
 * Route care plan completion to type-specific workflow (intake, triage, or CoCM).
 * @agentic @benchmark/core
 */ async function handleCarePlanCompletion(medplum, input) {
    const { carePlan, force = false, reason } = input;
    // Determine care plan type
    const carePlanType = getCarePlanType(carePlan);
    if (!carePlanType) {
        throw new Error(`Cannot complete care plan: Unknown care plan type for CarePlan/${carePlan.id}`);
    }
    // Route to type-specific completion workflow
    switch(carePlanType){
        case "intake":
            {
                const result = await handleIntakeCompletion(medplum, {
                    carePlan,
                    force,
                    reason
                });
                return {
                    carePlan: result.carePlan
                };
            }
        case "triage":
            {
                const result = await handleTriageCompletion(medplum, {
                    carePlan,
                    force,
                    reason
                });
                return {
                    carePlan: result.carePlan
                };
            }
        case "cocm":
            {
                const result = await handleCoCMCompletion(medplum, {
                    carePlan,
                    force,
                    reason
                });
                return {
                    carePlan: result.carePlan
                };
            }
        default:
            {
                throw new Error(`Unhandled care plan type: ${carePlanType}`);
            }
    }
}

/** Benchmark care-team participant SNOMED role for LCSW. @agentic @benchmark/core */ const CARE_TEAM_LCSW_ROLE_CODE = "224598009";
/** Benchmark care-team participant SNOMED role for patient member. @agentic @benchmark/core */ const CARE_TEAM_PATIENT_ROLE_CODE = "116154003";
/** Benchmark care-team category coding system. @agentic @benchmark/core */ const CARE_TEAM_CATEGORY_SYSTEM = "http://loinc.org";
/** Benchmark care-team category coding value. @agentic @benchmark/core */ const CARE_TEAM_CATEGORY_CODE = "LA28865-6";
/** Canonical name used for the patient benchmark care team. @agentic @benchmark/core */ const BENCHMARK_CARE_TEAM_NAME = "Benchmark Care Team";
/** Display hints used to detect legacy Benchmark LCSW entries. @agentic @benchmark/core */ const LEGACY_LCSW_DISPLAY_HINTS = [
    "lcsw",
    "licensed clinical social worker",
    "social worker",
    "case manager"
];
/**
 * Normalizes PractitionerRole references to full `PractitionerRole/{id}` format.
 * @agentic @benchmark/core
 */ const toPractitionerRoleReference = (practitionerRoleRef)=>practitionerRoleRef.startsWith("PractitionerRole/") ? practitionerRoleRef : `PractitionerRole/${practitionerRoleRef}`;
/**
 * Returns true when a Patient.generalPractitioner entry is tagged as Benchmark primary advocate.
 * Supports both current and legacy extension codes.
 * @agentic @benchmark/core
 */ const isBenchmarkPrimaryAdvocateGeneralPractitioner = (reference)=>reference.extension?.some((extension)=>extension.url === GP_ROLE_TYPE_EXTENSION && (extension.valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || extension.valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW)) ?? false;
/**
 * Determines whether a generalPractitioner reference most likely represents the legacy Benchmark primary advocate slot.
 * @agentic @benchmark/core
 */ const isLikelyLegacyBenchmarkPractitioner = (reference, intendedReference)=>{
    if (!reference.reference?.startsWith("PractitionerRole/")) {
        return false;
    }
    const gpRoleExtension = reference.extension?.find((extension)=>extension.url === GP_ROLE_TYPE_EXTENSION);
    if (gpRoleExtension?.valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || gpRoleExtension?.valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW) {
        return true;
    }
    if (gpRoleExtension?.valueCode) {
        return false;
    }
    if (reference.reference === intendedReference) {
        return true;
    }
    const normalizedDisplay = reference.display?.toLowerCase() ?? "";
    return LEGACY_LCSW_DISPLAY_HINTS.some((hint)=>normalizedDisplay.includes(hint));
};
/**
 * Replaces the Benchmark primary advocate slot in Patient.generalPractitioner while preserving other roles.
 * @agentic @benchmark/core
 */ const replacePatientBenchmarkPractitioner = (patient, practitionerRoleReference, practitionerRoleDisplay)=>{
    const existingReferences = patient.generalPractitioner ?? [];
    const replacementReference = {
        reference: practitionerRoleReference,
        display: practitionerRoleDisplay,
        extension: [
            {
                url: GP_ROLE_TYPE_EXTENSION,
                valueCode: GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE
            }
        ]
    };
    const existingTypedIndexes = existingReferences.map((reference, index)=>isBenchmarkPrimaryAdvocateGeneralPractitioner(reference) ? index : -1).filter((index)=>index >= 0);
    if (existingTypedIndexes.length > 0) {
        const firstTypedIndex = existingTypedIndexes[0];
        return existingReferences.flatMap((reference, index)=>{
            if (index === firstTypedIndex) {
                return [
                    replacementReference
                ];
            }
            if (isBenchmarkPrimaryAdvocateGeneralPractitioner(reference)) {
                return [];
            }
            return [
                reference
            ];
        });
    }
    // Legacy fallback: Benchmark role is commonly stored at index 1.
    if (existingReferences[1] && isLikelyLegacyBenchmarkPractitioner(existingReferences[1], practitionerRoleReference)) {
        return existingReferences.map((reference, index)=>index === 1 ? replacementReference : reference);
    }
    const insertIndex = Math.min(1, existingReferences.length);
    return [
        ...existingReferences.slice(0, insertIndex),
        replacementReference,
        ...existingReferences.slice(insertIndex)
    ];
};
/**
 * Returns true when the care-team participant represents an LCSW assignment.
 * @agentic @benchmark/core
 */ const isLcswCareTeamParticipant = (participant)=>participant.role?.some((role)=>role.coding?.some((coding)=>coding.code === CARE_TEAM_LCSW_ROLE_CODE)) ?? false;
/**
 * Ensures the selected practitioner exists as an LCSW participant without removing others.
 * @agentic @benchmark/core
 */ const upsertSelectedLcswParticipant = (participants, practitionerRoleReference, practitionerRoleDisplay)=>{
    let hasSelectedParticipant = false;
    const updatedParticipants = participants.map((participant)=>{
        if (isLcswCareTeamParticipant(participant) && participant.member?.reference === practitionerRoleReference) {
            hasSelectedParticipant = true;
            return {
                ...participant,
                member: {
                    ...participant.member,
                    reference: practitionerRoleReference,
                    display: practitionerRoleDisplay
                }
            };
        }
        return participant;
    });
    if (hasSelectedParticipant) {
        return updatedParticipants;
    }
    return [
        ...participants,
        buildLcswCareTeamParticipant(practitionerRoleReference, practitionerRoleDisplay)
    ];
};
/**
 * Builds a standard patient participant for Benchmark CareTeam creation.
 * @agentic @benchmark/core
 */ const buildPatientCareTeamParticipant = (patientId, patientDisplay)=>({
        role: [
            {
                coding: [
                    {
                        system: "http://snomed.info/sct",
                        code: CARE_TEAM_PATIENT_ROLE_CODE,
                        display: "Patient"
                    }
                ]
            }
        ],
        member: {
            reference: `Patient/${patientId}`,
            display: patientDisplay
        }
    });
/**
 * Builds the Benchmark LCSW participant entry used for care-team synchronization.
 * @agentic @benchmark/core
 */ const buildLcswCareTeamParticipant = (practitionerRoleReference, practitionerRoleDisplay)=>({
        role: [
            {
                coding: [
                    {
                        system: "http://snomed.info/sct",
                        code: CARE_TEAM_LCSW_ROLE_CODE,
                        display: "Licensed Clinical Social Worker"
                    }
                ]
            }
        ],
        member: {
            reference: practitionerRoleReference,
            display: practitionerRoleDisplay
        }
    });
/**
 * Selects the patient's Benchmark CareTeam from a list of care teams.
 * @agentic @benchmark/core
 */ const findBenchmarkCareTeam = (careTeams)=>careTeams.find((careTeam)=>careTeam.category?.[0]?.coding?.[0]?.code === CARE_TEAM_CATEGORY_CODE || careTeam.name === BENCHMARK_CARE_TEAM_NAME);
/**
 * Synchronizes schedule-intake selected practitioner onto patient GP + benchmark care team.
 * @agentic @benchmark/core
 */ const syncSelectedBenchmarkPractitioner = async (medplum, input)=>{
    const { patientId, practitionerRoleRef, practitionerRoleDisplay } = input;
    const patient = await medplum.readResource("Patient", patientId);
    const practitionerRoleReference = toPractitionerRoleReference(practitionerRoleRef);
    await medplum.updateResource({
        ...patient,
        generalPractitioner: replacePatientBenchmarkPractitioner(patient, practitionerRoleReference, practitionerRoleDisplay)
    });
    const careTeams = await medplum.searchResources("CareTeam", {
        subject: `Patient/${patientId}`
    });
    const benchmarkCareTeam = findBenchmarkCareTeam(careTeams);
    if (benchmarkCareTeam?.id) {
        await medplum.updateResource({
            ...benchmarkCareTeam,
            participant: upsertSelectedLcswParticipant(benchmarkCareTeam.participant ?? [], practitionerRoleReference, practitionerRoleDisplay)
        });
        return;
    }
    const patientDisplay = patient.name?.[0] ? `${patient.name[0].given?.[0] ?? ""} ${patient.name[0].family ?? ""}`.trim() : undefined;
    await medplum.createResource({
        resourceType: "CareTeam",
        status: "active",
        name: BENCHMARK_CARE_TEAM_NAME,
        subject: {
            reference: `Patient/${patientId}`,
            display: patientDisplay
        },
        category: [
            {
                coding: [
                    {
                        system: CARE_TEAM_CATEGORY_SYSTEM,
                        code: CARE_TEAM_CATEGORY_CODE,
                        display: "Longitudinal Care-Coordination"
                    }
                ]
            }
        ],
        participant: [
            buildPatientCareTeamParticipant(patientId, patientDisplay),
            buildLcswCareTeamParticipant(practitionerRoleReference, practitionerRoleDisplay)
        ]
    });
};
/**
 * Promote a patient to "engaged" when they complete their intake.
 *
 * Historically the engagement status only advanced to "engaged" when the whole
 * intake CarePlan completed. Because the intake protocol has activities after
 * "complete-intake" (finalize enrollment, check-in), a patient who finished
 * their intake encounter could remain "referred" indefinitely. This aligns the
 * engagement status with the domain definition of "engaged" (consented to
 * services, completed intake) at the moment intake is completed.
 *
 * Runs as a best-effort side effect: a failure here is logged but never blocks
 * activity completion, mirroring the other side effects in this workflow.
 * @agentic @benchmark/core
 */ async function promoteEngagementForIntakeCompletion(medplum, patientId) {
    try {
        const patient = await medplum.readResource("Patient", patientId);
        if (shouldEngagePatientOnIntakeCompletion(patient)) {
            await updatePatientEngagementStatus(medplum, patient, "engaged");
        }
    } catch (error) {
        // Best-effort side effect — log redacted metadata only, never the raw
        // error object (Medplum errors can embed patient-linked context).
        console.error("Failed to promote engagement status after intake completion:", toLogSafeError(error));
    }
}
/**
 * Handle care plan activity completion with automatic progression
 *
 * This workflow:
 * 1. Updates the specified activity to "completed" status
 * 2. Automatically advances the next activity to "ready" (if available)
 * 3. Checks if all activities are complete and triggers care plan completion if so
 * 4. Emits CarePlanActivityCompletedEvent with progression details
 *
 * @param medplum - MedplumClient instance
 * @param input - Activity completion data
 * @returns Completion result with progression status
 * @throws Error if workflow fails
 */ async function handleActivityCompletion(medplum, input) {
    const { carePlan, activityCode, taskId, statusReason, completionData, practitionerRole, documentedEncounter } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required for activity completion");
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "") || "";
    // Optional side effect for schedule-intake handoff:
    // synchronize selected practitioner on patient assignment artifacts.
    if (patientId && completionData?.patientBenchmarkPractitionerRoleRef && completionData?.patientBenchmarkPractitionerRoleDisplay) {
        await syncSelectedBenchmarkPractitioner(medplum, {
            patientId,
            practitionerRoleRef: completionData.patientBenchmarkPractitionerRoleRef,
            practitionerRoleDisplay: completionData.patientBenchmarkPractitionerRoleDisplay
        });
    }
    // Step 1: Update activity to completed
    const updatedCarePlan = await updateCarePlanActivity(medplum, {
        carePlan,
        activityCode,
        taskId,
        newStatus: "completed",
        statusReason,
        practitionerRole,
        documentedEncounter
    });
    // Completing intake advances the patient to "engaged". This does not depend
    // on the whole intake CarePlan completing (later activities like finalize
    // enrollment / check-in keep it active), so it must run here rather than in
    // the CarePlan-completion path. Legacy intake codes resolve to
    // "complete-intake" via the taxonomy map.
    if (patientId && resolveActivityCode$1(activityCode) === "complete-intake") {
        await promoteEngagementForIntakeCompletion(medplum, patientId);
    }
    // Step 2: Auto-advance next activity to ready
    let carePlanWithNextActivity = updatedCarePlan;
    let nextActivityAdvanced = false;
    try {
        const beforeVersionId = updatedCarePlan.meta?.versionId;
        const advancedCarePlan = await advanceToNextActivity(medplum, updatedCarePlan, activityCode, completionData, taskId);
        // Detect advancement by comparing CarePlan versionId (changes on server update)
        // or by checking for legacy detail-based changes
        const versionChanged = advancedCarePlan.meta?.versionId !== beforeVersionId;
        const detailStatusChanged = advancedCarePlan.activity?.some((activity, index)=>{
            const originalActivity = updatedCarePlan.activity?.[index];
            return activity.detail?.status && activity.detail.status !== originalActivity?.detail?.status;
        });
        if (versionChanged || detailStatusChanged) {
            nextActivityAdvanced = true;
            carePlanWithNextActivity = advancedCarePlan;
        }
    } catch (error) {
        console.error("Failed to advance next activity:", toLogSafeError(error));
    }
    // Step 3: Check if all activities are complete and trigger care plan completion.
    // Skip auto-completion for CoCM care plans — they can have activities added dynamically.
    // Single task fetch for completion check (reduced from 3 fetches in previous implementation).
    let carePlanCompleted = false;
    let finalCarePlan = carePlanWithNextActivity;
    const carePlanType = getCarePlanType(carePlanWithNextActivity);
    if (carePlanType !== "cocm" && carePlanWithNextActivity.id) {
        const tasksById = await buildCarePlanTasksMap(medplum, carePlanWithNextActivity, {
            requireTaskBackedActivities: false,
            suppressErrors: true
        });
        if (areAllActivitiesCompleted(carePlanWithNextActivity, tasksById)) {
            try {
                const completionResult = await handleCarePlanCompletion(medplum, {
                    carePlan: carePlanWithNextActivity,
                    force: false
                });
                carePlanCompleted = true;
                finalCarePlan = completionResult.carePlan;
            } catch (error) {
                console.error("Failed to complete care plan after all activities completed:", toLogSafeError(error));
                // Reconcile against latest server state to avoid false negatives when
                // completion succeeded remotely but local orchestration errored.
                const refreshedCarePlan = await medplum.readResource("CarePlan", carePlanWithNextActivity.id);
                if (refreshedCarePlan.status === "completed") {
                    carePlanCompleted = true;
                    finalCarePlan = refreshedCarePlan;
                } else {
                    throw new Error(`Failed to complete care plan after all activities completed: ${error instanceof Error ? error.message : "Unknown error"}`, {
                        cause: error
                    });
                }
            }
        }
    }
    // CoCM plans never auto-complete (activities are added dynamically), so the
    // completion path is also where a mis-advanced or archived-gap plan could be
    // left with no `ready` step. Reconcile to guarantee the step-based invariant:
    // exactly one open activity is `ready`. Idempotent with advanceToNextActivity.
    if (carePlanType === "cocm" && finalCarePlan.id) {
        finalCarePlan = await reconcileCarePlanReadyState(medplum, finalCarePlan);
    }
    // Step 4: Emit event
    if (!finalCarePlan.id) {
        throw new Error("Care plan ID is required after update");
    }
    const event = {
        carePlanId: finalCarePlan.id,
        carePlan: finalCarePlan,
        activityCode,
        patientId,
        nextActivityAdvanced,
        carePlanCompleted
    };
    globalEventEmitter.publish(DomainEventNames.CarePlanActivityCompleted, event);
    return {
        updatedCarePlan: finalCarePlan,
        nextActivityAdvanced,
        carePlanCompleted
    };
}

/**
 * Handle triage care plan cancellation with atomic Task updates.
 * @agentic @benchmark/core
 */ async function handleTriageCancellation(medplum, input) {
    return cancelOperationalCarePlan(medplum, {
        carePlan: input.carePlan,
        reason: input.reason,
        planTypeLabel: "triage"
    });
}

/**
 * Route care plan cancellation to type-specific workflow (intake, triage, or CoCM).
 * @agentic @benchmark/core
 */ async function handleCarePlanCancellation(medplum, input) {
    const { carePlan, reason } = input;
    // Determine care plan type
    const carePlanType = getCarePlanType(carePlan);
    if (!carePlanType) {
        throw new Error(`Cannot cancel care plan: Unknown care plan type for CarePlan/${carePlan.id}`);
    }
    // Route to type-specific cancellation workflow
    switch(carePlanType){
        case "intake":
            {
                const result = await handleIntakeCancellation(medplum, {
                    carePlan,
                    reason
                });
                return {
                    carePlan: result.carePlan
                };
            }
        case "triage":
            {
                const result = await handleTriageCancellation(medplum, {
                    carePlan,
                    reason
                });
                return {
                    carePlan: result.carePlan
                };
            }
        case "cocm":
            {
                const result = await handleCoCMCancellation(medplum, {
                    carePlan,
                    reason
                });
                return {
                    carePlan: result.carePlan
                };
            }
        default:
            {
                throw new Error(`Unhandled care plan type: ${carePlanType}`);
            }
    }
}

/**
 * Onboard a new patient into the care system. Creates intake care plan and advances first activity.
 * @agentic @benchmark/core
 */ async function onboardNewPatient(medplum, input) {
    const { patient, practitionerRole } = input;
    // Check if patient already has an intake care plan
    const existingCarePlans = await medplum.searchResources("CarePlan", {
        subject: `Patient/${patient.id}`,
        category: "intake",
        status: "active,draft"
    });
    if (existingCarePlans.length > 0) {
        // Patient already has an intake care plan, return existing
        const existingPlan = existingCarePlans[0];
        if (!existingPlan) {
            throw new Error("Expected care plan not found");
        }
        return {
            patient,
            carePlan: existingPlan
        };
    }
    // Set engagement status to "referred" if not already set
    let updatedPatient = patient;
    const currentStatus = getPatientEngagementStatus$1(patient);
    if (!currentStatus) {
        // No engagement status found, set to "referred"
        updatedPatient = await updatePatientEngagementStatus(medplum, patient, "referred");
    }
    // Create intake care plan via action (creates in active status)
    const intakeCarePlan = await createCarePlan(medplum, {
        patient: updatedPatient,
        type: "intake",
        practitionerRole
    });
    // Auto-advance first activity to in-progress (paired with creation)
    const carePlanWithFirstActivity = await advanceFirstActivityToReady(medplum, intakeCarePlan);
    return {
        patient: updatedPatient,
        carePlan: carePlanWithFirstActivity
    };
}

/**
 * Pause a patient's care plan via transaction bundle. Creates pause flag, updates care plans and tasks.
 * @agentic @benchmark/core
 */ async function pausePatient(medplum, input) {
    const { patientId, reason, durationDays } = input;
    // Step 1: Fetch patient
    const patient = await medplum.readResource("Patient", patientId);
    // Step 2: Search for active care plans and open tasks
    const [activeCarePlans, openTasks] = await Promise.all([
        getActiveCarePlans(medplum, patientId),
        findOpenTasksForPatient(medplum, patientId)
    ]);
    // Filter tasks that have due dates (only these can be adjusted)
    const tasksToAdjust = openTasks.filter((task)=>Boolean(task.id && task.restriction?.period?.end));
    // Step 3: Build transaction bundle
    const bundle = buildPausePatientBundle({
        patient,
        reason,
        durationDays,
        activeCarePlans,
        tasksToAdjust
    });
    // Step 4: Submit bundle atomically
    const result = await submitBundle(medplum, bundle);
    // Step 5: Extract results from transaction response
    const pauseFlag = extractSingleResourceFromTransactionResult(result, "Flag");
    const carePlans = extractResourcesFromTransactionResult(result, "CarePlan");
    const carePlan = carePlans.length > 0 ? carePlans[0] || null : null;
    // Step 5b: Pausing clears any outstanding service-approval notices — the
    // member's services no longer need approving (E4). Best-effort: a notification
    // cleanup failure must never fail the pause. No-op when the feature is off.
    for (const openTask of openTasks){
        const isApprovalTask = (openTask.code?.coding ?? []).some((coding)=>coding.code === APPROVE_SELECTED_SERVICES_CODE);
        if (!openTask.id || !isApprovalTask) continue;
        try {
            await clearServiceApprovalNotificationForTask(medplum, openTask.id);
        } catch (error) {
            console.error("Failed to clear service-approval notification on pause:", toLogSafeError(error));
        }
    }
    // Step 6: Emit event (after successful transaction)
    const event = {
        patientId,
        reason,
        durationDays
    };
    globalEventEmitter.publish(DomainEventNames.PatientPaused, event);
    return {
        pauseFlag,
        carePlans,
        carePlan,
        tasksAdjusted: tasksToAdjust.length > 0
    };
}

/**
 * Returns a copy of an on-hold CarePlan restored to its pre-pause status.
 *
 * The pre-pause status marker written by the pause bundle is consumed and
 * removed; plans held before that marker existed fall back to "active". A
 * resume note is appended so the plan's narrative shows the hold ending — the
 * pause path writes an "On Hold" note, and a hold with no matching resume is
 * indistinguishable from a plan that is still paused.
 * @agentic @benchmark/core
 */ function buildResumedCarePlan(carePlan, options = {}) {
    const { resumedAt = new Date(), reason } = options;
    const { extension: _heldExtensions, ...withoutExtensions } = carePlan;
    const remainingExtensions = (carePlan.extension || []).filter((ext)=>ext.url !== CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL);
    return {
        ...withoutExtensions,
        ...remainingExtensions.length > 0 ? {
            extension: remainingExtensions
        } : {},
        status: getPrePauseCarePlanStatus$1(carePlan) ?? "active",
        note: [
            ...carePlan.note || [],
            {
                text: reason ? `Resumed: ${reason}` : "Resumed",
                time: resumedAt.toISOString()
            }
        ]
    };
}

/**
 * Statuses a paused care plan may legitimately be resumed into. A plan that was
 * completed, cancelled, revoked, or in an unknown state before the pause is not
 * restored by the resume path.
 * @agentic @benchmark/core
 */ const CARE_PLAN_RESUMABLE_STATUSES = [
    "draft",
    "active"
];
/**
 * Returns the status a care plan held before it was paused, when the pause
 * bundle recorded one. Plans held before the marker existed return undefined,
 * and callers fall back to "active" (the historical resume behavior).
 * @agentic @benchmark/core
 */ function getPrePauseCarePlanStatus(carePlan) {
    const recorded = carePlan.extension?.find((ext)=>ext.url === CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL)?.valueCode;
    // "on-hold" would resume the plan into the state we are leaving; ignore it.
    if (!recorded || recorded === "on-hold") {
        return undefined;
    }
    return CARE_PLAN_RESUMABLE_STATUSES.includes(recorded) ? recorded : undefined;
}

/**
 * Returns engagement status with version (v1/v2) and migration suggestion.
 * @agentic @benchmark/core
 */ function getPatientEngagementStatus(patient) {
    if (!patient.meta?.tag) return null;
    // Prefer V2 system tags first (if they exist, they're the most current)
    const v2SystemTag = patient.meta.tag.find((tag)=>tag.system === PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v2SystemTag?.code) {
        const statusOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v2SystemTag.code);
        if (statusOption) {
            return {
                code: statusOption.code,
                display: statusOption.display,
                system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v2"
            };
        }
        // Fallback: return V2 tag even if code doesn't match options (shouldn't happen, but prevents "Unknown")
        return {
            code: v2SystemTag.code,
            display: v2SystemTag.display || v2SystemTag.code,
            system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
            version: "v2"
        };
    }
    // Check for tags under V1 system URL (which may contain V2 codes)
    const v1SystemTag = patient.meta.tag.find((tag)=>tag.system === LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v1SystemTag?.code) {
        // First try to match as V2 code (current codes can be stored under V1 system)
        const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v1SystemTag.code);
        if (v2Option) {
            return {
                code: v2Option.code,
                display: v2Option.display,
                system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v2"
            };
        }
        // Fall back to V1 code matching
        const v1Option = LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v1SystemTag.code);
        if (v1Option) {
            return {
                code: v1Option.code,
                display: v1Option.display,
                system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v1",
                shouldMigrate: true
            };
        }
        // Fallback: return V1 tag even if code doesn't match options (prevents "Unknown")
        return {
            code: v1SystemTag.code,
            display: v1SystemTag.display || v1SystemTag.code,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
            version: "v1",
            shouldMigrate: true
        };
    }
    return null;
}

/**
 * Returns true when a pause Flag's `period.end` is strictly in the past.
 * A flag without a `period.end` is treated as indefinite (never expired).
 * @agentic @benchmark/core
 */ function isPauseFlagExpired(flag, now = new Date()) {
    const end = flag.period?.end;
    if (!end) {
        return false;
    }
    const endMs = new Date(end).getTime();
    return Number.isFinite(endMs) && endMs < now.getTime();
}
/**
 * Resolves a period bound to epoch ms, using `fallback` when absent/invalid.
 */ const periodBound = (value, fallback)=>{
    if (!value) {
        return fallback;
    }
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : fallback;
};
/**
 * Returns true when two pause Flags' periods intersect. A missing start is
 * treated as open-started and a missing end as open-ended.
 * @agentic @benchmark/core
 */ function pauseFlagsOverlap(a, b) {
    const aStart = periodBound(a.period?.start, -Infinity);
    const aEnd = periodBound(a.period?.end, Infinity);
    const bStart = periodBound(b.period?.start, -Infinity);
    const bEnd = periodBound(b.period?.end, Infinity);
    return aStart <= bEnd && bStart <= aEnd;
}
/**
 * Merges the periods of overlapping pause Flags into the span they cover
 * together, so a chain of overlapping pauses reads as one continuous episode.
 * @agentic @benchmark/core
 */ function mergePauseFlagPeriods(flags) {
    const starts = flags.map((flag)=>flag.period?.start).filter((value)=>Boolean(value)).map((value)=>new Date(value).getTime()).filter((ms)=>Number.isFinite(ms));
    const ends = flags.map((flag)=>flag.period?.end).filter((value)=>Boolean(value)).map((value)=>new Date(value).getTime()).filter((ms)=>Number.isFinite(ms));
    return {
        ...starts.length > 0 ? {
            start: new Date(Math.min(...starts)).toISOString()
        } : {},
        ...ends.length > 0 ? {
            end: new Date(Math.max(...ends)).toISOString()
        } : {}
    };
}

/**
 * Thrown when {@link unpausePatient} is asked to unpause only an elapsed pause
 * and finds one that is still live.
 * @agentic @benchmark/core
 */ class PauseStillLiveError extends Error {
    constructor(){
        super("Patient has a live pause; refusing to unpause");
        this.name = "PauseStillLiveError";
    }
}
const MS_PER_DAY = 1000 * 60 * 60 * 24;
/** Parses a period bound to epoch ms, or undefined when absent/unparseable. */ function toEpochMs(value) {
    if (!value) {
        return undefined;
    }
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : undefined;
}
/**
 * The span the patient was actually held under, across every active pause
 * Flag. Overlapping and duplicate pauses are treated as one continuous
 * episode running from the earliest start to the latest end, which is how the
 * patient experienced it and how the flag-expiry hygiene tool reconciles them.
 * @agentic @benchmark/core
 */ function resolveEffectivePauseWindow(pauseFlags, now = new Date()) {
    const starts = pauseFlags.map((flag)=>toEpochMs(flag.period?.start)).filter((ms)=>ms !== undefined);
    const ends = pauseFlags.map((flag)=>toEpochMs(flag.period?.end)).filter((ms)=>ms !== undefined);
    const startMs = starts.length > 0 ? Math.min(...starts) : now.getTime();
    const actualDurationDays = Math.ceil((now.getTime() - startMs) / MS_PER_DAY);
    // A pause with no recorded end is indefinite: nothing was planned, so
    // nothing is handed back on resume.
    const plannedDurationDays = ends.length > 0 && starts.length > 0 ? Math.ceil((Math.max(...ends) - startMs) / MS_PER_DAY) : 0;
    return {
        plannedDurationDays,
        actualDurationDays
    };
}
/**
 * Unpause a patient's care plan via transaction bundle. Resumes care plans and recalculates task due dates.
 * @agentic @benchmark/core
 */ async function unpausePatient(medplum, patient, options = {}) {
    // Validate patient has ID
    if (!patient.id) {
        throw new Error("Patient must have an id to unpause");
    }
    // Step 1: Find every active pause flag. All of them are deactivated below —
    // leaving a duplicate active would keep the patient reading as paused.
    const activeFlags = await fetchExistingActiveFlags(medplum, patient.id);
    const pauseFlags = activeFlags.filter((flag)=>isPauseFlag(flag) && !!flag.id);
    if (pauseFlags.length === 0) {
        throw new Error("No active pause flag found for patient");
    }
    // Validated against the very read the bundle is built from, so nothing can
    // slip in between the check and the write.
    if (options.requireAllPausesElapsed && pauseFlags.some((flag)=>!isPauseFlagExpired(flag))) {
        throw new PauseStillLiveError();
    }
    // Step 2: Calculate actual vs planned pause duration
    const { plannedDurationDays, actualDurationDays } = resolveEffectivePauseWindow(pauseFlags);
    // Step 3: Search for on-hold care plans and open tasks
    const [onHoldCarePlans, openTasks] = await Promise.all([
        getOnHoldCarePlans(medplum, patient.id),
        findOpenTasksForPatient(medplum, patient.id)
    ]);
    // Filter tasks that have due dates (only these can be recalculated)
    const tasksToRecalculate = openTasks.filter((task)=>Boolean(task.id && task.restriction?.period?.end));
    // Step 4: Build transaction bundle
    const bundle = buildUnpausePatientBundle({
        patient: patient,
        pauseFlags,
        onHoldCarePlans,
        tasksToRecalculate,
        plannedDurationDays,
        actualDurationDays,
        resumeReason: options.resumeReason
    });
    // Step 5: Submit bundle atomically
    const result = await submitBundle(medplum, bundle);
    // Step 6: Extract results from transaction response
    const carePlans = extractResourcesFromTransactionResult(result, "CarePlan");
    const resumedCarePlan = carePlans.length > 0 ? carePlans[0] || null : null;
    // Step 7: Emit event (after successful transaction)
    const event = {
        patientId: patient.id
    };
    globalEventEmitter.publish(DomainEventNames.PatientUnpaused, event);
    return {
        resumedCarePlan,
        resumedCarePlans: carePlans,
        // Whether due dates actually moved, not whether any were eligible. The
        // bundle skips task writes entirely once the pause has run its full
        // course, so counting candidates here would report work that never
        // happened.
        tasksRecalculated: extractResourcesFromTransactionResult(result, "Task").length > 0
    };
}

/** Reason recorded on care plans resumed because the pause ran its course. */ const ELAPSED_RESUME_REASON = "pause elapsed";
/** Reason recorded on care plans that were held with no pause Flag left. */ const ORPHAN_RESUME_REASON = "pause ended without unpause";
const patientIdFromSubject = (reference)=>reference?.startsWith("Patient/") ? reference.replace("Patient/", "") : null;
const groupBySubject = (resources, hasId)=>{
    const grouped = new Map();
    for (const resource of resources){
        const patientId = patientIdFromSubject(resource.subject?.reference);
        if (!patientId || !hasId(resource)) {
            continue;
        }
        const existing = grouped.get(patientId) ?? [];
        existing.push(resource);
        grouped.set(patientId, existing);
    }
    return grouped;
};
const errorMessage = (error)=>error instanceof Error ? error.message : String(error);
/**
 * Returns the terminating engagement status a patient carries, if any.
 *
 * Offboarding revokes a patient's **active** care plans; it does not look at
 * `on-hold` ones. So a patient paused and then declined keeps held plans and
 * an active pause Flag, and resuming them later would restart care for someone
 * who left the program. Reconciliation stops at those patients and reports
 * them instead — revoking the plans is a clinical decision, not a janitor's.
 */ async function findTerminatingStatus(medplum, patientId) {
    const patient = await medplum.readResource("Patient", patientId);
    const status = getPatientEngagementStatus(patient)?.code;
    return status && isTerminatingEngagementStatus(status) ? status : undefined;
}
/**
 * Deactivates a patient's elapsed pause Flags while leaving a live pause in
 * place. Elapsed Flags overlapping the live pause are folded into it first, so
 * the surviving Flag describes one continuous episode rather than leaving a
 * gap where the patient was in fact still paused.
 */ async function reconcileStillPausedPatient(medplum, options) {
    const { expiredFlags, liveFlags, dryRun } = options;
    // Plan the merges first so a live pause overlapped by several elapsed ones
    // is written once, spanning all of them.
    const merges = new Map();
    for (const expired of expiredFlags){
        const target = liveFlags.find((live)=>pauseFlagsOverlap(expired, live));
        if (!target) {
            continue; // stale leftover from an earlier episode — just deactivate it
        }
        const existing = merges.get(target.id) ?? {
            flag: target,
            span: [
                target
            ]
        };
        existing.span.push(expired);
        merges.set(target.id, existing);
    }
    if (!dryRun) {
        for (const { flag, span } of merges.values()){
            await updateResource(medplum, {
                ...flag,
                period: {
                    ...flag.period,
                    ...mergePauseFlagPeriods(span)
                }
            });
        }
        for (const expired of expiredFlags){
            // Preserve the historical `period.end` — the pause genuinely ended then.
            await updateResource(medplum, {
                ...expired,
                status: "inactive"
            });
        }
    }
    return {
        flagsCombined: merges.size === 0 ? 0 : [
            ...merges.values()
        ].reduce((sum, merge)=>sum + merge.span.length - 1, 0),
        flagsDeactivated: expiredFlags.length
    };
}
/**
 * Converge every patient whose pause state has drifted.
 *
 * Three disjoint cases, evaluated per patient:
 *
 *   1. Every active pause Flag has elapsed → unpause through the canonical
 *      workflow (deactivates the Flags, resumes held care plans, recalculates
 *      task due dates, emits the domain event).
 *   2. At least one pause is still live → leave the patient paused; only
 *      tidy up the elapsed Flags.
 *   3. Care plans held with no active pause Flag at all → resume the plans.
 *      The Flag-driven passes cannot see these patients, and they are the
 *      terminal state of a pause that was never reversed.
 *
 * Defaults to a dry run: the summary reports what would change and nothing is
 * written.
 * @agentic @benchmark/core
 */ async function reconcilePatientPauses(medplum, options = {}) {
    const { dryRun = true, includeOrphanCarePlans = true, now = new Date(), onProgress } = options;
    const activePauseFlags = await findAllActivePauseFlags(medplum);
    const flagsByPatient = groupBySubject(activePauseFlags, (flag)=>Boolean(flag.id));
    const flagCandidates = [
        ...flagsByPatient.entries()
    ].filter(([, flags])=>flags.some((flag)=>isPauseFlagExpired(flag, now)));
    const onHoldCarePlans = includeOrphanCarePlans ? await findAllOnHoldCarePlans(medplum) : [];
    const plansByPatient = groupBySubject(onHoldCarePlans, (plan)=>Boolean(plan.id));
    // A patient with any active pause Flag is either legitimately paused or is
    // handled by the Flag-driven pass below; only the rest are orphans.
    const orphanCandidates = [
        ...plansByPatient.entries()
    ].filter(([patientId])=>!flagsByPatient.has(patientId));
    const driftBefore = {
        patientsWithElapsedPauses: flagCandidates.length,
        elapsedPauseFlags: flagCandidates.reduce((sum, [, flags])=>sum + flags.filter((flag)=>isPauseFlagExpired(flag, now)).length, 0),
        patientsWithOrphanedHolds: orphanCandidates.length,
        orphanedOnHoldCarePlans: orphanCandidates.reduce((sum, [, plans])=>sum + plans.length, 0)
    };
    const results = [];
    const total = flagCandidates.length + orphanCandidates.length;
    let processed = 0;
    const advance = ()=>onProgress?.(++processed, total);
    for (const [patientId, flags] of flagCandidates){
        const expiredFlags = flags.filter((flag)=>isPauseFlagExpired(flag, now));
        const liveFlags = flags.filter((flag)=>!isPauseFlagExpired(flag, now));
        const base = {
            patientId,
            patientName: flags[0]?.subject?.display,
            expiredFlagIds: expiredFlags.map((flag)=>flag.id),
            periodEnd: expiredFlags.map((flag)=>flag.period?.end).filter((end)=>Boolean(end)).sort().pop(),
            timestamp: new Date().toISOString()
        };
        // CASE 2 — still paused. Tidy the elapsed Flags, change nothing else.
        if (liveFlags.length > 0) {
            try {
                const { flagsCombined, flagsDeactivated } = await reconcileStillPausedPatient(medplum, {
                    expiredFlags,
                    liveFlags,
                    dryRun
                });
                const combineNote = flagsCombined > 0 ? `, combined ${flagsCombined} into the live pause` : "";
                results.push({
                    ...base,
                    outcome: "reconciled",
                    reason: `${dryRun ? "Would deactivate" : "Deactivated"} ${flagsDeactivated} elapsed pause Flag(s)${combineNote} (patient stays paused)`,
                    carePlansResumed: 0,
                    tasksRecalculated: 0,
                    flagsDeactivated,
                    flagsCombined
                });
            } catch (error) {
                results.push({
                    ...base,
                    outcome: "error",
                    reason: errorMessage(error),
                    carePlansResumed: 0,
                    tasksRecalculated: 0,
                    flagsDeactivated: 0,
                    flagsCombined: 0
                });
            }
            advance();
            continue;
        }
        // CASE 1 — every pause elapsed. Unpause, and report the downstream effects
        // using the same reads the unpause workflow performs.
        try {
            const terminatingStatus = await findTerminatingStatus(medplum, patientId);
            if (terminatingStatus) {
                results.push({
                    ...base,
                    outcome: "skipped",
                    reason: `Patient was offboarded as ${terminatingStatus}; not restarting their care plans`,
                    carePlansResumed: 0,
                    tasksRecalculated: 0,
                    flagsDeactivated: 0,
                    flagsCombined: 0
                });
                advance();
                continue;
            }
            const heldCarePlans = await getOnHoldCarePlans(medplum, patientId);
            const carePlansResumed = heldCarePlans.length;
            // Unpausing hands back only the days of the pause the patient never
            // spent paused. Every Flag on this path has already elapsed, so that is
            // always zero and no task due date moves. Reporting the count of dated
            // open tasks here would claim work the run does not do.
            const tasksRecalculated = 0;
            if (!dryRun) {
                // The candidate set came from a project-wide snapshot that may be
                // minutes old. Rather than re-check here and hand the decision to a
                // function that fetches the Flags again — discarding the check and
                // deactivating whatever it finds — let the unpause validate against
                // the same read it writes from.
                const patient = {
                    resourceType: "Patient",
                    id: patientId
                };
                await unpausePatient(medplum, patient, {
                    resumeReason: ELAPSED_RESUME_REASON,
                    requireAllPausesElapsed: true
                });
            }
            results.push({
                ...base,
                outcome: "unpaused",
                reason: `${dryRun ? "Would unpause" : "Unpaused"} patient: ${carePlansResumed} care plan(s), ${tasksRecalculated} task due date(s), ${expiredFlags.length} pause Flag(s)`,
                carePlansResumed,
                tasksRecalculated,
                flagsDeactivated: expiredFlags.length,
                flagsCombined: 0
            });
        } catch (error) {
            results.push({
                ...base,
                outcome: error instanceof PauseStillLiveError ? "skipped" : "error",
                reason: error instanceof PauseStillLiveError ? "Patient was paused again after this run started; leaving them paused" : errorMessage(error),
                carePlansResumed: 0,
                tasksRecalculated: 0,
                flagsDeactivated: 0,
                flagsCombined: 0
            });
        }
        advance();
    }
    // CASE 3 — care plans held with no pause Flag left to expire. Only the plans
    // are touched: there is no Flag to deactivate, and task due dates were
    // already extended by the full pause duration when the pause was applied.
    for (const [patientId, plans] of orphanCandidates){
        const base = {
            patientId,
            patientName: plans[0]?.subject?.display,
            expiredFlagIds: [],
            timestamp: new Date().toISOString()
        };
        try {
            // Patient pause is not the only writer of `CarePlan.status = "on-hold"`
            // — `holdCarePlan` writes it too (no production caller today), and the
            // CLI triage migration copies an on-hold status onto a plan it creates.
            // So attribute before writing rather than assuming: resume only when the
            // patient has pause history, or a plan carries the marker the pause
            // bundle stamps. Anything else is reported for a human, never resumed.
            //
            // Pause history is the weaker of the two signals — it attributes any
            // held plan on a patient who was ever paused. It is here because plans
            // held before the marker existed have nothing else to go on. Once no
            // pre-marker holds remain, drop it and gate on the marker alone.
            const [pauseHistory, currentPauseFlags, terminatingStatus] = await Promise.all([
                findPatientPauseFlagHistory(medplum, patientId),
                findPatientActivePauseFlags(medplum, patientId),
                findTerminatingStatus(medplum, patientId)
            ]);
            if (terminatingStatus) {
                results.push({
                    ...base,
                    outcome: "skipped",
                    reason: `Patient was offboarded as ${terminatingStatus}; not restarting their care plans`,
                    carePlansResumed: 0,
                    tasksRecalculated: 0,
                    flagsDeactivated: 0,
                    flagsCombined: 0
                });
                advance();
                continue;
            }
            // Re-read guards the same snapshot race as CASE 1: a patient paused
            // since the sweep started legitimately has held care plans now.
            if (currentPauseFlags.length > 0) {
                results.push({
                    ...base,
                    outcome: "skipped",
                    reason: "Patient was paused after this run started; their care plans are held on purpose",
                    carePlansResumed: 0,
                    tasksRecalculated: 0,
                    flagsDeactivated: 0,
                    flagsCombined: 0
                });
                advance();
                continue;
            }
            const attributable = plans.filter((plan)=>pauseHistory.length > 0 || getPrePauseCarePlanStatus(plan) !== undefined);
            if (attributable.length === 0) {
                results.push({
                    ...base,
                    outcome: "skipped",
                    reason: `${plans.length} care plan(s) on hold cannot be attributed to a pause (no pause Flag history); leaving them for review`,
                    carePlansResumed: 0,
                    tasksRecalculated: 0,
                    flagsDeactivated: 0,
                    flagsCombined: 0
                });
                advance();
                continue;
            }
            if (!dryRun) {
                // One transaction per patient: resuming three of a patient's four
                // plans and failing the fourth would leave them half-paused, and the
                // error result would report none of it.
                const bundle = {
                    resourceType: "Bundle",
                    type: "transaction",
                    entry: attributable.map((plan)=>({
                            request: {
                                method: "PUT",
                                url: `CarePlan/${plan.id}`
                            },
                            resource: buildResumedCarePlan(plan, {
                                reason: ORPHAN_RESUME_REASON
                            })
                        }))
                };
                await submitBundle(medplum, bundle);
            }
            results.push({
                ...base,
                outcome: "orphan-resumed",
                reason: `${dryRun ? "Would resume" : "Resumed"} ${attributable.length} care plan(s) left on hold with no pause Flag`,
                carePlansResumed: attributable.length,
                tasksRecalculated: 0,
                flagsDeactivated: 0,
                flagsCombined: 0
            });
        } catch (error) {
            results.push({
                ...base,
                outcome: "error",
                reason: errorMessage(error),
                carePlansResumed: 0,
                tasksRecalculated: 0,
                flagsDeactivated: 0,
                flagsCombined: 0
            });
        }
        advance();
    }
    const sumBy = (key)=>results.reduce((sum, result)=>sum + result[key], 0);
    const countBy = (outcome)=>results.filter((result)=>result.outcome === outcome).length;
    return {
        dryRun,
        driftBefore,
        results,
        totalUnpaused: countBy("unpaused"),
        totalReconciled: countBy("reconciled"),
        totalOrphanResumed: countBy("orphan-resumed"),
        totalSkipped: countBy("skipped"),
        totalErrors: countBy("error"),
        totalCarePlansResumed: sumBy("carePlansResumed"),
        totalTasksRecalculated: sumBy("tasksRecalculated"),
        totalFlagsDeactivated: sumBy("flagsDeactivated"),
        totalFlagsCombined: sumBy("flagsCombined"),
        timestamp: new Date().toISOString()
    };
}

/** True when the Flag is a screener follow-up pause Flag. @agentic @benchmark/core */ const isScreenerFollowUpPauseFlag = (flag)=>(flag.code?.coding ?? []).some((coding)=>coding.system === FLAG_LOCAL_CODE_SYSTEM && coding.code === SCREENER_FOLLOWUPS_PAUSED_FLAG_CODE);
/**
 * Build an active screener follow-up pause Flag. `period.start` is the pause
 * date; there is no `period.end` until a person resumes.
 * @agentic @benchmark/core
 */ function buildScreenerFollowUpPauseFlag(patient, reason, now = new Date()) {
    return {
        resourceType: "Flag",
        status: "active",
        code: {
            coding: [
                {
                    system: FLAG_LOCAL_CODE_SYSTEM,
                    code: SCREENER_FOLLOWUPS_PAUSED_FLAG_CODE,
                    display: "Screener follow-ups paused"
                }
            ],
            text: "Screener follow-ups paused"
        },
        category: [
            {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/flag-category",
                        code: "admin",
                        display: "Administrative"
                    }
                ],
                text: "Administrative"
            }
        ],
        subject: {
            reference: `Patient/${patient.id}`,
            display: getPatientDisplayName(patient)
        },
        period: {
            start: now.toISOString()
        },
        extension: [
            {
                url: PAUSE_FLAG_REASON_EXTENSION_URL,
                valueString: reason
            }
        ]
    };
}
/**
 * Pause automated screener follow-ups for a patient and publish FlagCreated.
 * @agentic @benchmark/core
 */ async function pauseScreenerFollowUps(medplum, input) {
    const reason = input.reason.trim();
    if (!reason) {
        throw new Error("A pause reason is required");
    }
    const flag = await createResource(medplum, buildScreenerFollowUpPauseFlag(input.patient, reason));
    const event = {
        flagId: flag.id ?? "",
        flag,
        patientId: input.patient.id
    };
    globalEventEmitter.publish(DomainEventNames.FlagCreated, event);
    return flag;
}
/**
 * Resume automated screener follow-ups: deactivate every active pause Flag
 * (a user pause and a cron fail-closed pause can coexist) and publish
 * FlagUpdated per Flag.
 * @agentic @benchmark/core
 */ async function resumeScreenerFollowUps(medplum, input) {
    // `Flag.code` is not a Medplum search parameter (it returns 400), so the
    // pause code is filtered in memory.
    const active = await medplum.searchResources("Flag", {
        subject: `Patient/${input.patientId}`,
        status: "active",
        // A pause left active past the default page of 20 would keep the patient paused.
        _count: "1000"
    });
    const end = new Date().toISOString();
    const resumed = [];
    for (const flag of active.filter(isScreenerFollowUpPauseFlag)){
        if (!flag.id) continue;
        const updated = await updateResource(medplum, {
            ...flag,
            id: flag.id,
            status: "inactive",
            period: {
                ...flag.period,
                end
            }
        });
        const event = {
            flagId: flag.id,
            flag: updated,
            patientId: input.patientId
        };
        globalEventEmitter.publish(DomainEventNames.FlagUpdated, event);
        resumed.push(updated);
    }
    return resumed;
}

/**
 * Update patient coverage (insurance) information. Handles self-pay, primary/secondary insurance, and subscribers.
 * @agentic @benchmark/core
 */ async function updatePatientCoverage(medplum, patient, formData) {
    if (!patient.id) {
        throw new Error("Patient must have an id");
    }
    const patientId = patient.id;
    const saveUpdatedCoverage = async (coverage)=>{
        const saved = await updateResource(medplum, coverage);
        const event = {
            coverageId: coverage.id,
            coverage: saved,
            patientId
        };
        globalEventEmitter.publish(DomainEventNames.CoverageUpdated, event);
    };
    const saveNewCoverage = async (coverage)=>{
        const created = await createResource(medplum, coverage);
        if (!created.id) {
            throw new Error("Coverage ID is required after creation");
        }
        const event = {
            coverageId: created.id,
            coverage: created,
            patientId
        };
        globalEventEmitter.publish(DomainEventNames.CoverageCreated, event);
    };
    // Get existing coverage resources
    const searchResult = await medplum.searchResources("Coverage", {
        beneficiary: `Patient/${patient.id}`
    });
    const existingCoverages = Array.isArray(searchResult) ? searchResult : [];
    // Filter to only active coverages for update logic
    // Cancelled coverages are preserved as historical records - we don't reactivate them
    const activeCoverages = existingCoverages.filter((cov)=>cov.status === "active");
    const sortedActiveCoverages = [
        ...activeCoverages
    ].sort((a, b)=>{
        const orderA = a.order ?? Number.POSITIVE_INFINITY;
        const orderB = b.order ?? Number.POSITIVE_INFINITY;
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        return (a.id ?? "").localeCompare(b.id ?? "");
    });
    // Handle self-pay patients - cancel all existing coverages
    if (formData["self-pay"]) {
        // Cancel all active coverages for self-pay patients
        for (const coverage of activeCoverages){
            if (coverage.id) {
                const updatedCoverage = {
                    ...coverage,
                    id: coverage.id,
                    status: "cancelled",
                    period: {
                        ...coverage.period,
                        end: localCalendarDateTodayISO()
                    }
                };
                await saveUpdatedCoverage(updatedCoverage);
            }
        }
        return; // Skip all insurance processing for self-pay patients
    }
    // Update primary insurance
    // Only update if there's an existing ACTIVE coverage, otherwise create new
    if (formData["primary-insurance-provider"] || formData["primary-subscriber-id"]) {
        const primaryCoverage = sortedActiveCoverages[0];
        if (primaryCoverage?.id) {
            // Handle subscriber reference for non-self relationships
            let subscriberRef = primaryCoverage.subscriber;
            if (formData["primary-relationship-to-subscriber"] !== "self" && formData["primary-subscriber-first-name"] && formData["primary-subscriber-last-name"]) {
                subscriberRef = {
                    reference: await upsertSubscriber(medplum, {
                        patient,
                        subscriberFirstName: formData["primary-subscriber-first-name"],
                        subscriberLastName: formData["primary-subscriber-last-name"],
                        subscriberDob: formData["primary-subscriber-dob"],
                        subscriberPhone: formData["primary-subscriber-phone"],
                        relationship: formData["primary-relationship-to-subscriber"],
                        existingSubscriberRef: primaryCoverage.subscriber?.reference
                    })
                };
            } else if (formData["primary-relationship-to-subscriber"] === "self") {
                // For self relationships, subscriber is the patient
                subscriberRef = {
                    reference: `Patient/${patient.id}`,
                    display: getPatientDisplayName(patient)
                };
            }
            // Build Coverage.class for InsurancePlan if provided
            // Preserve existing classes and add/update plan class
            const coverageClass = formData["primary-insurance-plan"] ? (()=>{
                const existingClasses = primaryCoverage.class || [];
                const nonPlanClasses = existingClasses.filter((cls)=>cls.type?.coding?.[0]?.code !== "plan");
                return [
                    ...nonPlanClasses,
                    {
                        type: {
                            coding: [
                                {
                                    system: "http://terminology.hl7.org/CodeSystem/coverage-class",
                                    code: "plan",
                                    display: "Plan"
                                }
                            ]
                        },
                        value: formData["primary-insurance-plan"],
                        ...formData["primary-insurance-plan-display"] && {
                            name: formData["primary-insurance-plan-display"]
                        }
                    }
                ];
            })() : primaryCoverage.class;
            // Update existing active primary coverage
            const updatedCoverage = {
                ...primaryCoverage,
                id: primaryCoverage.id,
                order: COVERAGE_APPLICABILITY_PRIMARY,
                payor: formData["primary-insurance-provider"] ? formData["primary-insurance-provider-display"] ? [
                    buildOrganizationReference(toOrganizationId(formData["primary-insurance-provider"]), formData["primary-insurance-provider-display"])
                ] : [
                    {
                        reference: `Organization/${formData["primary-insurance-provider"]}`
                    }
                ] : primaryCoverage.payor,
                class: coverageClass,
                subscriberId: formData["primary-subscriber-id"] || primaryCoverage.subscriberId,
                relationship: formData["primary-relationship-to-subscriber"] ? {
                    coding: [
                        {
                            code: formData["primary-relationship-to-subscriber"],
                            display: formData["primary-relationship-to-subscriber"]
                        }
                    ]
                } : primaryCoverage.relationship,
                subscriber: subscriberRef
            };
            await saveUpdatedCoverage(updatedCoverage);
        } else if (formData["primary-insurance-provider"]) {
            // Handle subscriber reference for new coverage
            let subscriberRef;
            if (formData["primary-relationship-to-subscriber"] === "self") {
                subscriberRef = {
                    reference: `Patient/${patient.id}`,
                    display: getPatientDisplayName(patient)
                };
            } else if (formData["primary-subscriber-first-name"] && formData["primary-subscriber-last-name"]) {
                subscriberRef = {
                    reference: await upsertSubscriber(medplum, {
                        patient,
                        subscriberFirstName: formData["primary-subscriber-first-name"],
                        subscriberLastName: formData["primary-subscriber-last-name"],
                        subscriberDob: formData["primary-subscriber-dob"],
                        subscriberPhone: formData["primary-subscriber-phone"],
                        relationship: formData["primary-relationship-to-subscriber"]
                    })
                };
            }
            // Build Coverage.class for InsurancePlan if provided
            const newCoverageClass = formData["primary-insurance-plan"] ? [
                {
                    type: {
                        coding: [
                            {
                                system: "http://terminology.hl7.org/CodeSystem/coverage-class",
                                code: "plan",
                                display: "Plan"
                            }
                        ]
                    },
                    value: formData["primary-insurance-plan"],
                    ...formData["primary-insurance-plan-display"] && {
                        name: formData["primary-insurance-plan-display"]
                    }
                }
            ] : undefined;
            // Create new primary coverage
            const newCoverage = {
                resourceType: "Coverage",
                status: "active",
                order: COVERAGE_APPLICABILITY_PRIMARY,
                beneficiary: {
                    reference: `Patient/${patient.id}`,
                    display: getPatientDisplayName(patient)
                },
                payor: formData["primary-insurance-provider-display"] ? [
                    buildOrganizationReference(toOrganizationId(formData["primary-insurance-provider"]), formData["primary-insurance-provider-display"])
                ] : [
                    {
                        reference: `Organization/${formData["primary-insurance-provider"]}`
                    }
                ],
                class: newCoverageClass,
                subscriberId: formData["primary-subscriber-id"],
                relationship: formData["primary-relationship-to-subscriber"] ? {
                    coding: [
                        {
                            code: formData["primary-relationship-to-subscriber"],
                            display: formData["primary-relationship-to-subscriber"]
                        }
                    ]
                } : undefined,
                subscriber: subscriberRef
            };
            await saveNewCoverage(newCoverage);
        }
    }
    // Handle secondary insurance
    // Only update if there's an existing ACTIVE secondary coverage, otherwise create new
    if (formData["has-secondary-insurance"]) {
        if (formData["secondary-insurance-provider"] || formData["secondary-subscriber-id"]) {
            // Find existing ACTIVE secondary coverage (index > 0 in active coverages)
            const secondaryCoverage = sortedActiveCoverages.length > 1 ? sortedActiveCoverages[1] : undefined;
            if (secondaryCoverage?.id) {
                // Handle subscriber reference for secondary coverage
                let subscriberRef = secondaryCoverage.subscriber;
                if (formData["secondary-relationship-to-subscriber"] !== "self" && formData["secondary-subscriber-first-name"] && formData["secondary-subscriber-last-name"]) {
                    subscriberRef = {
                        reference: await upsertSubscriber(medplum, {
                            patient,
                            subscriberFirstName: formData["secondary-subscriber-first-name"],
                            subscriberLastName: formData["secondary-subscriber-last-name"],
                            subscriberDob: formData["secondary-subscriber-dob"],
                            subscriberPhone: formData["secondary-subscriber-phone"],
                            relationship: formData["secondary-relationship-to-subscriber"],
                            existingSubscriberRef: secondaryCoverage.subscriber?.reference
                        })
                    };
                } else if (formData["secondary-relationship-to-subscriber"] === "self") {
                    // For self relationships, subscriber is the patient
                    subscriberRef = {
                        reference: `Patient/${patient.id}`,
                        display: getPatientDisplayName(patient)
                    };
                }
                // Build Coverage.class for InsurancePlan if provided
                // Preserve existing classes and add/update plan class
                const secondaryCoverageClass = formData["secondary-insurance-plan"] ? (()=>{
                    const existingClasses = secondaryCoverage.class || [];
                    const nonPlanClasses = existingClasses.filter((cls)=>cls.type?.coding?.[0]?.code !== "plan");
                    return [
                        ...nonPlanClasses,
                        {
                            type: {
                                coding: [
                                    {
                                        system: "http://terminology.hl7.org/CodeSystem/coverage-class",
                                        code: "plan",
                                        display: "Plan"
                                    }
                                ]
                            },
                            value: formData["secondary-insurance-plan"],
                            ...formData["secondary-insurance-plan-display"] && {
                                name: formData["secondary-insurance-plan-display"]
                            }
                        }
                    ];
                })() : secondaryCoverage.class;
                // Update existing active secondary coverage
                const updatedCoverage = {
                    ...secondaryCoverage,
                    id: secondaryCoverage.id,
                    order: COVERAGE_APPLICABILITY_SECONDARY,
                    payor: formData["secondary-insurance-provider"] ? formData["secondary-insurance-provider-display"] ? [
                        buildOrganizationReference(toOrganizationId(formData["secondary-insurance-provider"]), formData["secondary-insurance-provider-display"])
                    ] : [
                        {
                            reference: `Organization/${formData["secondary-insurance-provider"]}`
                        }
                    ] : secondaryCoverage.payor,
                    class: secondaryCoverageClass,
                    subscriberId: formData["secondary-subscriber-id"] || secondaryCoverage.subscriberId,
                    relationship: formData["secondary-relationship-to-subscriber"] ? {
                        coding: [
                            {
                                code: formData["secondary-relationship-to-subscriber"],
                                display: formData["secondary-relationship-to-subscriber"]
                            }
                        ]
                    } : secondaryCoverage.relationship,
                    subscriber: subscriberRef
                };
                await saveUpdatedCoverage(updatedCoverage);
            } else if (formData["secondary-insurance-provider"]) {
                // Handle subscriber reference for new secondary coverage
                let subscriberRef;
                if (formData["secondary-relationship-to-subscriber"] === "self") {
                    subscriberRef = {
                        reference: `Patient/${patient.id}`,
                        display: getPatientDisplayName(patient)
                    };
                } else if (formData["secondary-subscriber-first-name"] && formData["secondary-subscriber-last-name"]) {
                    subscriberRef = {
                        reference: await upsertSubscriber(medplum, {
                            patient,
                            subscriberFirstName: formData["secondary-subscriber-first-name"],
                            subscriberLastName: formData["secondary-subscriber-last-name"],
                            subscriberDob: formData["secondary-subscriber-dob"],
                            subscriberPhone: formData["secondary-subscriber-phone"],
                            relationship: formData["secondary-relationship-to-subscriber"]
                        })
                    };
                }
                // Build Coverage.class for InsurancePlan if provided
                const newSecondaryCoverageClass = formData["secondary-insurance-plan"] ? [
                    {
                        type: {
                            coding: [
                                {
                                    system: "http://terminology.hl7.org/CodeSystem/coverage-class",
                                    code: "plan",
                                    display: "Plan"
                                }
                            ]
                        },
                        value: formData["secondary-insurance-plan"],
                        ...formData["secondary-insurance-plan-display"] && {
                            name: formData["secondary-insurance-plan-display"]
                        }
                    }
                ] : undefined;
                // Create new secondary coverage
                const newCoverage = {
                    resourceType: "Coverage",
                    status: "active",
                    order: COVERAGE_APPLICABILITY_SECONDARY,
                    beneficiary: {
                        reference: `Patient/${patient.id}`,
                        display: getPatientDisplayName(patient)
                    },
                    payor: formData["secondary-insurance-provider-display"] ? [
                        buildOrganizationReference(toOrganizationId(formData["secondary-insurance-provider"]), formData["secondary-insurance-provider-display"])
                    ] : [
                        {
                            reference: `Organization/${formData["secondary-insurance-provider"]}`
                        }
                    ],
                    class: newSecondaryCoverageClass,
                    subscriberId: formData["secondary-subscriber-id"],
                    relationship: formData["secondary-relationship-to-subscriber"] ? {
                        coding: [
                            {
                                code: formData["secondary-relationship-to-subscriber"],
                                display: formData["secondary-relationship-to-subscriber"]
                            }
                        ]
                    } : undefined,
                    subscriber: subscriberRef
                };
                await saveNewCoverage(newCoverage);
            }
        }
    } else {
        // Mark secondary coverage as cancelled if has-secondary-insurance is false
        // This preserves the historical record while indicating it's no longer active
        if (sortedActiveCoverages.length > 1) {
            for (const secondaryCoverage of sortedActiveCoverages.slice(1)){
                if (secondaryCoverage.id) {
                    const updatedCoverage = {
                        ...secondaryCoverage,
                        id: secondaryCoverage.id,
                        status: "cancelled",
                        // Add a period end date to mark when it was cancelled
                        period: {
                            ...secondaryCoverage.period,
                            end: localCalendarDateTodayISO()
                        }
                    };
                    await saveUpdatedCoverage(updatedCoverage);
                }
            }
        }
    }
}

/**
 * Update a patient with all related resources. Uses transaction bundle for Patient/RelatedPerson/CareTeam; coverage and conditions separately.
 * @agentic @benchmark/core
 */ async function updatePatientWithRelatedResources(medplum, input) {
    const { formData, existingPatient } = input;
    if (!existingPatient.id) {
        throw new Error("Existing patient must have an id");
    }
    // Step 1: Search for existing related resources
    const [existingCoverages, existingRelatedPersons, existingCareTeams] = await Promise.all([
        medplum.searchResources("Coverage", {
            beneficiary: `Patient/${existingPatient.id}`
        }),
        medplum.searchResources("RelatedPerson", {
            patient: `Patient/${existingPatient.id}`
        }),
        medplum.searchResources("CareTeam", {
            subject: `Patient/${existingPatient.id}`
        })
    ]);
    // Find emergency contact
    const existingEmergencyContact = existingRelatedPersons.find((rp)=>rp.relationship?.[0]?.coding?.[0]?.code === "emergency");
    // Find Benchmark Care Team
    const existingCareTeam = existingCareTeams.find((ct)=>ct.category?.[0]?.coding?.[0]?.code === "LA28865-6" || ct.name === "Benchmark Care Team");
    // Step 2: Build transaction bundle for Patient + RelatedPerson + CareTeam
    const bundle = buildUpdatePatientBundle({
        formData,
        existingPatient,
        existingCoverages: Array.isArray(existingCoverages) ? existingCoverages : [],
        existingEmergencyContact,
        existingCareTeam,
        authorReference: input.authorReference
    });
    // Step 3: Submit bundle atomically
    const result = await submitBundle(medplum, bundle);
    // Step 4: Extract updated patient from result
    const updatedPatient = extractSingleResourceFromTransactionResult(result, "Patient");
    if (!updatedPatient.id) {
        throw new Error("Patient ID is required after update");
    }
    // Step 5: Update Coverage separately (complex logic)
    // This handles primary/secondary insurance, self-pay, subscriber relationships
    // TODO(#982): Refactor updatePatientCoverage to support transaction bundles for full atomicity
    try {
        await updatePatientCoverage(medplum, updatedPatient, formData);
    } catch (error) {
        console.error("Error updating patient coverage:", error);
    // Don't fail the entire update if coverage update fails
    }
    // Step 6: Update Conditions (ICD-10 codes)
    try {
        await updatePatientConditions(medplum, updatedPatient, formData["icd10-codes"] || []);
    } catch (error) {
        console.error("Error updating patient conditions:", error);
    // Don't fail the entire update if condition update fails
    }
    const event = {
        patientId: updatedPatient.id,
        patient: updatedPatient
    };
    globalEventEmitter.publish(DomainEventNames.PatientUpdated, event);
    return updatedPatient;
}
/**
 * Update patient conditions (ICD-10 codes). Adds new conditions and marks removed ones inactive.
 * @agentic @benchmark/core
 */ async function updatePatientConditions(medplum, patient, formICD10Codes) {
    const patientId = patient.id;
    if (!patientId) {
        return;
    }
    // Fetch existing conditions
    const existingConditions = await searchProblemListConditions(medplum, patientId);
    // Determine which codes to add and which conditions to deactivate
    const existingCodes = new Set(existingConditions.map((c)=>c.code?.coding?.find((coding)=>coding.system === ICD10_CM_SYSTEM)?.code).filter(Boolean));
    const formCodes = new Set(formICD10Codes.map((c)=>c.code));
    // Codes to add (in form but not in existing)
    const codesToAdd = formICD10Codes.filter((code)=>!existingCodes.has(code.code));
    // Conditions to deactivate (in existing but not in form)
    const conditionsToDeactivate = existingConditions.filter((condition)=>{
        const icd10Code = condition.code?.coding?.find((coding)=>coding.system === ICD10_CM_SYSTEM)?.code;
        return icd10Code && !formCodes.has(icd10Code);
    });
    // Create new conditions
    for (const icd10Code of codesToAdd){
        const newCondition = buildProblemListCondition({
            patient,
            icd10Code
        });
        const created = await createCondition(medplum, newCondition);
        if (!created.id) {
            throw new Error("Condition ID is required after creation");
        }
        const event = {
            conditionId: created.id,
            condition: created,
            patientId
        };
        globalEventEmitter.publish(DomainEventNames.ConditionCreated, event);
    }
    // Deactivate removed conditions (mark as inactive, don't delete)
    for (const condition of conditionsToDeactivate){
        if (!condition.id) continue;
        const updatedCondition = {
            ...condition,
            id: condition.id,
            clinicalStatus: {
                coding: [
                    {
                        system: CONDITION_CLINICAL_STATUS_SYSTEM,
                        code: "inactive",
                        display: "Inactive"
                    }
                ]
            }
        };
        const saved = await updateCondition(medplum, updatedCondition);
        const event = {
            conditionId: condition.id,
            condition: saved,
            patientId
        };
        globalEventEmitter.publish(DomainEventNames.ConditionUpdated, event);
    }
}

export { PauseStillLiveError, advanceFirstActivityToReady, advanceToNextActivity, buildScreenerFollowUpPauseFlag, cancelOperationalCarePlan, createOperationalCarePlan, createPatientWithRelatedResources, enrollInCoCM, enrollInIntake, enrollInTriage, getOrCreateCoCMCarePlan, handleActivityCompletion, handleCarePlanCancellation, handleCarePlanCompletion, handleCoCMCancellation, handleCoCMCompletion, handleIntakeCancellation, handleIntakeCompletion, handleTriageCancellation, handleTriageCompletion, isScreenerFollowUpPauseFlag, onboardNewPatient, pausePatient, pauseScreenerFollowUps, reconcilePatientPauses, resolveEffectivePauseWindow, resumeScreenerFollowUps, unpausePatient, updatePatientCoverage, updatePatientWithRelatedResources };
